import { projectServiceBenefitsRewardAdjustmentRows, resolveServiceBenefitsReviewEvidence } from "@/app/booking/rewardsIntegration/serviceBenefitsSummaryProjection";
import { frontendSingleAppointmentPublicationReadIsCurrent, readFrontendSingleAppointmentPublicationsV1, type FrontendSingleAppointmentPublicationSource } from "./frontendSingleAppointmentPublicationV1";
import { readFrontendRewardAppointmentActivitiesV1, joinFrontendRewardAppointmentGiftCardActivityV1 } from "@/app/booking/rewards/frontendRewardAppointmentActivity";
import { subscribeFrontendRewardOperationsV1 } from "@/app/booking/rewards/frontendRewardOperationLedger";
import { FRONTEND_MEMBERSHIP_PURCHASE_LEDGER_EVENT, readFrontendMembershipPurchaseSales, type FrontendMembershipPurchaseSaleV1 } from "@/app/commerce/memberships/frontendMembershipPurchaseLedger";
import { productRewardFingerprint } from "@/app/commerce/rewards/productRewardAllocation";
import { readFrontendBookingActivityPreviews, subscribeFrontendBookingActivityPreviews } from "./frontendBookingActivityPreview";
import { appointmentFormsPublicationReadIsCurrent, projectActivityAppointmentFormsPublicationV1, readAppointmentFormsPublicationsV1, type AppointmentFormsPublicationSource } from "./activityAppointmentFormsPublicationV1";
import { createFrontendDescendantStateOwnerKey } from "@/app/hooks/frontendDescendantStateOwner";
import { customerFormsOverviewReadHasPublicationProof, customerFormsOverviewReadIsCurrent } from "@/app/profile/forms/customerFormsProvider";
import { captureCustomerOwnedSessionStorageMutationProof, customerOwnedSessionStorageMutationProofIsCurrent } from "@/app/booking/core/customerOwnedSessionStorageMutationCoordinator";
import { readStoredCustomerAuthSessionStrict } from "@/app/services/authApi";
import { readFrontendGiftCardAppointmentHoldsV1 } from "@/app/commerce/packages/frontendPackagePurchaseLedgerV2";
import { createFrontendGiftCardAppointmentActivity } from "./frontendGiftCardAppointmentActivityV1";
export { createFrontendGiftCardAppointmentActivity } from "./frontendGiftCardAppointmentActivityV1";
import { frontendGiftCardProviderOwnerSnapshot } from "@/app/commerce/giftCards/frontendGiftCardProviderOwner";
import { readFrontendGiftCardCurrentPresentationsV1, type FrontendGiftCardCurrentPresentationV1 } from "@/app/commerce/giftCards/frontendGiftCardCurrentPresentationV1";
import {
  ACTIVITY_PAGE_SIZE_MAX,
  ACTIVITY_SCHEMA_VERSION,
  ACTIVITY_SEARCH_QUERY_MAX_LENGTH,
  ACTIVITY_TABS,
  type ActivityActionCapability,
  type ActivityActionDestination,
  type ActivityActionKind,
  type ActivityAppointmentAddOn,
  type ActivityAppointmentFormAssignment,
  type ActivityAppointmentLine,
  type ActivityAppointmentStatus,
  type ActivityAttentionState,
  type ActivityCompleteness,
  type ActivityFreshness,
  type ActivityGroup,
  type ActivityGroupSnapshot,
  type ActivityItem,
  type ActivityLocationSnapshot,
  type ActivityMoney,
  type ActivityPaymentEvidence,
  type ActivityPackageEvent,
  type ActivityPackageGroupProductBenefit,
  type ActivityPackageGroupServiceBenefit,
  type ActivityPreviewMedia,
  type ActivityPricingBreakdown,
  type ActivityProvider,
  type ActivityProviderRequest,
  type ActivityProviderResult,
  type ActivityTabRootProviderRequest,
  type ActivityRefundEvidence,
  type ActivitySelectedPointerState,
  type ActivitySnapshot,
  type ActivityStaffSnapshot,
  type ActivityTab,
  type AppointmentActivityItem,
  type GiftCardActivityItem,
  type MembershipActivityItem,
  type PackageActivityItem,
  type ProductActivityItem,
  type WaitlistActivityItem,
} from "./activityContracts";
import { FRONTEND_ACTIVITY_FIXTURE_CUSTOMER_ID } from "./activityMutationProvider";
import {
  resolveActivityRebook,
  validateActivityRebookCatalogSnapshot,
  type ActivityRebookCatalogSnapshot,
  type ActivityRebookResolution,
} from "./activityRebookResolver";
import {
  createActivityPointer,
  isActivityIdentifier,
  parseActivityPointer,
  validateActivityItem,
  validateActivitySnapshot,
} from "./activityValidation";
import { CUSTOMER_ACCOUNT_JOURNEY_FIXTURE } from "@/app/customerAccountJourneyFixture";
import { readFrontendCombinedPackageOwnedInventoryV2 } from "@/app/commerce/packages/frontendPackageOwnedLegacyV2";
import { readFrontendPackageGiftClaimForOwned } from "@/app/commerce/packages/frontendPackageGiftClaimStore";
import { packageGiftTransferMatchesOwned, readFrontendPackageGiftTransfers } from "@/app/commerce/packages/frontendPackageGiftTransferStore";
import { samePackageContractData } from "@/app/commerce/packages/packagePurchaseContractData";
import { readFrontendPackagePurchaseSaleV2, readFrontendPackageAtomicReservationsV3 } from "@/app/commerce/packages/frontendPackagePurchaseLedgerV2";
import { projectFrontendPackageAtomicMembershipV3, validateFrontendPackageAtomicRowV3, type FrontendPackageAtomicRowV3 } from "@/app/booking/packages/frontendPackageSessionAtomicLedgerV3";
import { resolvePackageSessionReceiptAttributionV1 } from "@/app/booking/packages/packageSessionReceiptAttributionV1";
import { buildAppointmentSchedulePlan } from "@/app/booking/scheduling/appointmentSchedulePlan";
import { createActivityAppointmentPackageRedemptionV1 } from "./activityPackageRedemption";
import type { PackageOwnedSnapshotV2 } from "@/app/commerce/packages/packageOwnedSnapshotV2";
import { canReadFrontendProductCurrentOrders, FRONTEND_PRODUCT_CURRENT_ORDER_FRESHNESS_WINDOW_MS, readFrontendProductCurrentOrdersV1 } from "@/app/commerce/products/frontendProductOrderLedgerV1";
import { projectProductCurrentOrderActivityV1 } from "@/app/commerce/products/productCurrentOrder";
import { getPackageBenefitReferenceCatalog } from "@/app/commerce/packages/packageBenefitReferenceCatalog";
import { projectPackagePurchaseBenefits } from "@/app/commerce/packages/packagePurchaseBenefitPresentation";
import { frontendCustomerFormsProvider } from "@/app/profile/forms/customerFormsProvider";
import type { CustomerFormsProvider } from "@/app/profile/forms/customerFormsContract";
import { validateCustomerFormsOverviewPayload } from "@/app/profile/forms/customerFormsValidation";
import { projectActivityCurrentForms } from "./activityFormsCurrentProjection";
import { CUSTOMER_FORMS_ACTIVITY_REQUIRED_FIXTURE } from "@/app/profile/forms/customerFormsFixtures";
import { buildCustomerFormAssignmentPath } from "@/app/profile/forms/customerFormsRoutes";
import { buildSpecialistServicesPath } from "@/app/specialists/routes";
import type {
  ClientGiftCardActivity,
  ClientGiftCardDetail,
} from "@/app/commerce/giftCards/giftCardWalletProvider";
import type {
  MembershipActivityAppointmentSummary,
  MembershipActivityDetailEvent,
  MembershipActivityDetailV1,
} from "@/app/commerce/memberships/membershipActivityDetail";

export const FRONTEND_ACTIVITY_PROVIDER_ID = "frontend-fixture-activity-v2";
export const FRONTEND_ACTIVITY_DATA_REVISION =
  "activity-fixture-v2-2026-09-04-memberships";
export const FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS = Date.UTC(
  2026,
  7,
  31,
  9,
  0,
  0,
);

export const FRONTEND_ACTIVITY_FIXTURE_SCENARIOS = Object.freeze([
  "empty",
  "single",
  "many",
  "upcoming",
  "past",
  "cancelled",
  "today",
  "future",
  "old",
  "awaiting-confirmation",
  "confirmed",
  "completed",
  "cancelled-by-customer",
  "cancelled-by-business",
  "declined",
  "no-show",
  "rescheduled",
  "in-progress-hardening",
  "single-service",
  "multi-service",
  "membership-redemption",
  "add-ons",
  "bundle",
  "group-booking",
  "participant-booking",
  "any-professional",
  "unavailable-specialist",
  "unavailable-location",
  "deposit",
  "full-payment",
  "pay-later",
  "card-on-file",
  "gift-card-payment",
  "split-payment",
  "refund-pending",
  "refund-completed",
  "free-cancellation",
  "cancellation-fee",
  "actions-allowed",
  "actions-blocked",
  "receipt-available",
  "receipt-unavailable",
  "waitlist",
  "waitlist-offered",
  "waitlist-expired",
  "form-required",
  "discounted",
  "gift-card",
  "membership",
  "membership-one-session",
  "membership-unlimited",
  "membership-exhausted",
  "membership-paused",
  "membership-past-due",
  "membership-cancel-at-period-end",
  "membership-cancelled",
  "membership-expired",
  "membership-online-off",
  "membership-fixed",
  "membership-fixed-no-early-cancel",
  "membership-many",
  "product",
  "product-delivery-placed",
  "product-shipped",
  "product-ready-for-pickup",
  "product-completed",
  "product-cancelled",
  "product-refund-pending",
  "product-refund-completed",
  "product-partial-refund",
  "product-processing-hardening",
  "product-multi-line",
  "product-no-image",
  "product-no-policy",
  "product-invoice-unavailable",
  "product-unavailable-location",
  "product-many",
  "package",
  "stale",
  "partial",
  "loading",
  "error",
  "provider-unavailable",
  "auth-expired",
  "customer-mismatch",
  "pagination",
  "handoff-a",
  "handoff-b",
] as const);

export type FrontendActivityFixtureScenario =
  (typeof FRONTEND_ACTIVITY_FIXTURE_SCENARIOS)[number];

export const FRONTEND_ACTIVITY_FIXTURE_CATALOG: readonly {
  scenario: FrontendActivityFixtureScenario;
  covers: readonly string[];
}[] = Object.freeze(
  FRONTEND_ACTIVITY_FIXTURE_SCENARIOS.map((scenario) =>
    Object.freeze({ scenario, covers: Object.freeze([scenario]) }),
  ),
);

const FIXTURE_TIME_ZONE = "Europe/Bucharest";
const PACKAGE_SESSION_APPOINTMENT_FIXTURE = Object.freeze({
  activityId: "appointment-volera-package-session-1",
  pointer: "appointment~appointment-volera-package-session-1" as const,
  reference: "VOL-PACKAGE-SESSION-1",
  startsAtEpochMs: Date.UTC(2026, 8, 3, 9),
  destinationPath:
    "/activity?details=appointment%7Eappointment-volera-package-session-1",
});

function fixtureLocalDateTime(epochMs: number, timeZone = FIXTURE_TIME_ZONE) {
  const values = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(new Date(epochMs))
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );

  return Object.freeze({
    localDate: `${values.year}-${values.month}-${values.day}`,
    localTime: `${values.hour}:${values.minute}`,
  });
}
const COMPLETE: ActivityCompleteness = Object.freeze({
  status: "complete",
  missingFields: Object.freeze([]) as readonly [],
});
const PARTIAL: ActivityCompleteness = Object.freeze({
  status: "partial",
  missingFields: Object.freeze(["payment-evidence"]),
});
const FIXTURE_PUBLICATION_ROTATION_MS = 15 * 60 * 1_000;
const FIXTURE_PUBLICATION_VALIDITY_MS = 2 * FIXTURE_PUBLICATION_ROTATION_MS;
const FRESH: ActivityFreshness = Object.freeze({
  status: "fresh",
  observedAtEpochMs: FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS,
  staleAtEpochMs: FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS + FIXTURE_PUBLICATION_VALIDITY_MS,
  reason: null,
});
const STALE: ActivityFreshness = Object.freeze({
  status: "stale",
  observedAtEpochMs:
    FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS - 24 * 60 * 60 * 1_000,
  staleAtEpochMs: FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS - 23 * 60 * 60 * 1_000,
  reason: "provider-marked-stale",
});

function fixtureFreshnessAt(
  observedAtEpochMs: number,
  status: "fresh" | "stale",
): ActivityFreshness | null {
  if (
    !Number.isSafeInteger(observedAtEpochMs) ||
    observedAtEpochMs < 0 ||
    observedAtEpochMs > 8_640_000_000_000_000 - FIXTURE_PUBLICATION_VALIDITY_MS
  ) {
    return null;
  }

  const publicationEpochMs =
    Math.floor(observedAtEpochMs / FIXTURE_PUBLICATION_ROTATION_MS) * FIXTURE_PUBLICATION_ROTATION_MS;

  if (status === "fresh") {
    // Rotation keeps equal publications stable across delayed cursor reads.
    // A second validity window guarantees at least one full booking-preview
    // lease even when Activity is first opened just before a bucket boundary.
    // Captured sources never slide or refresh their expiry during a journey.
    return Object.freeze({
      status: "fresh",
      observedAtEpochMs: publicationEpochMs,
      staleAtEpochMs: publicationEpochMs + FIXTURE_PUBLICATION_VALIDITY_MS,
      reason: null,
    });
  }

  const staleObservedAtEpochMs = Math.max(
    0,
    publicationEpochMs - 24 * 60 * 60 * 1_000,
  );
  return Object.freeze({
    status: "stale",
    observedAtEpochMs: staleObservedAtEpochMs,
    staleAtEpochMs: Math.max(
      staleObservedAtEpochMs,
      publicationEpochMs - 23 * 60 * 60 * 1_000,
    ),
    reason: "provider-marked-stale",
  });
}

function withCurrentFixturePublication(
  item: ActivityItem,
  freshness: ActivityFreshness,
): ActivityItem {
  if (item.activityType === "membership") {
    return membership(
      item.ownership.customerAccountId,
      item.activityId === "membership-volera-1"
        ? "active"
        : item.activityId.slice("membership-volera-".length) as MembershipFixtureVariant,
      freshness,
    );
  }
  if (
    item.activityType !== "waitlist" ||
    item.offeredSlot.status !== "available"
  ) {
    return Object.freeze({ ...item, freshness });
  }

  const offeredSlotEpochMs = freshness.staleAtEpochMs + 60 * 60 * 1_000;
  const offeredSlotLocal = fixtureLocalDateTime(offeredSlotEpochMs);

  return Object.freeze({
    ...item,
    freshness,
    attentionState: Object.freeze({
      status: "action-required" as const,
      reason: "confirm-offered-slot" as const,
      dueAtEpochMs: freshness.staleAtEpochMs,
    }),
    offeredSlot: Object.freeze({
      ...item.offeredSlot,
      offeredAtEpochMs: Math.max(
        0,
        freshness.observedAtEpochMs - 5 * 60 * 1_000,
      ),
      expiresAtEpochMs: freshness.staleAtEpochMs,
      slot: Object.freeze({
        ...item.offeredSlot.slot,
        occurredAtEpochMs: offeredSlotEpochMs,
        localDate: offeredSlotLocal.localDate,
        localTime: offeredSlotLocal.localTime,
      }),
    }),
  });
}

const NO_ATTENTION: ActivityAttentionState = Object.freeze({
  status: "none",
  reason: null,
  dueAtEpochMs: null,
});

const BUSINESS = Object.freeze({
  ...CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.business,
  imageUrl: "/images/widget/architectural-background.png",
});

const LOCATION = Object.freeze({
  availability: "available",
  ...CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.location,
}) satisfies ActivityLocationSnapshot;

const UNAVAILABLE_LOCATION: ActivityLocationSnapshot = Object.freeze({
  availability: "unavailable",
  locationId: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.location.locationId,
  name: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.location.name,
  reason: "closed",
});

const PREVIEW_MEDIA = Object.freeze([
  Object.freeze({
    mediaId: "media-glow-room-location-1",
    imageUrl:
      "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=960&q=82",
    owner: Object.freeze({
      kind: "location" as const,
      locationId: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.location.locationId,
    }),
  }),
  Object.freeze({
    mediaId: "media-glow-room-business-1",
    imageUrl:
      "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?auto=format&fit=crop&w=960&q=82",
    owner: Object.freeze({
      kind: "business" as const,
      businessId: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.business.businessId,
    }),
  }),
  Object.freeze({
    mediaId: "media-glow-room-location-2",
    imageUrl:
      "https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?auto=format&fit=crop&w=960&q=82",
    owner: Object.freeze({
      kind: "location" as const,
      locationId: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.location.locationId,
    }),
  }),
]) as ActivityPreviewMedia;

const STAFF_MARA: ActivityStaffSnapshot = Object.freeze({
  resolution: "specific",
  userLocationId: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.specialist.userLocationId,
  displayName: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.specialist.displayName,
  imageUrl: "/images/widget/placeholders/avatar-placeholder.png",
  availability: "available",
});

const STAFF_DARIA: ActivityStaffSnapshot = Object.freeze({
  resolution: "specific",
  userLocationId: "user-location-glow-room-support",
  displayName: "Diana Mureșan",
  imageUrl: "/images/widget/placeholders/avatar-placeholder.png",
  availability: "available",
});

const STAFF_ANY: ActivityStaffSnapshot = Object.freeze({
  resolution: "any-professional",
  userLocationId: null,
  displayName: null,
  imageUrl: null,
  availability: "available",
});

const STAFF_UNAVAILABLE: ActivityStaffSnapshot = Object.freeze({
  resolution: "unavailable",
  userLocationId: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.specialist.userLocationId,
  displayName: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.specialist.displayName,
  imageUrl: null,
  availability: "unavailable",
});

const rebookCatalogValidation = validateActivityRebookCatalogSnapshot({
  catalogRevision: "activity-rebook-catalog-2026-08-31",
  completeness: COMPLETE,
  freshness: FRESH,
  locations: [
    {
      locationId: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.location.locationId,
      available: true,
      public: true,
      onlineBookingEnabled: true,
      assignments: [
        {
          userLocationId:
            CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.specialist.userLocationId,
          available: true,
          onlineBookingEnabled: true,
          safeSelectionPath: buildSpecialistServicesPath({
            locationId: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.location.locationId,
            userLocationId:
              CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.specialist.userLocationId,
          }),
          services: [
            {
              serviceId: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.service.serviceId,
              onlineBookable: true,
              variantIds: [],
              addOns: [
                {
                  groupId: "andrada-makeup-extras",
                  addOnId: "premium-skin-prep",
                  onlineBookable: true,
                },
              ],
            },
            {
              serviceId: "andrada-brow-shape",
              onlineBookable: true,
              variantIds: [],
              addOns: [],
            },
            {
              serviceId: "andrada-day-makeup",
              onlineBookable: true,
              variantIds: [],
              addOns: [],
            },
          ],
          bundles: [
            {
              bundleId: "andrada-brow-lash-package",
              schedule: "parallel",
              onlineBookable: true,
              children: [
                {
                  childId: "andrada-brow-lamination-bundle-step",
                  serviceId: "andrada-brow-lamination",
                  onlineBookable: true,
                  addOns: [],
                },
                {
                  childId: "andrada-lash-lift-bundle-step",
                  serviceId: "andrada-lash-lift",
                  onlineBookable: true,
                  addOns: [],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
} satisfies ActivityRebookCatalogSnapshot);

if (!rebookCatalogValidation.valid) {
  throw new Error("Invalid frontend Activity rebook catalog fixture");
}

export const FRONTEND_ACTIVITY_REBOOK_CATALOG = rebookCatalogValidation.catalog;

export function resolveFrontendFixtureActivityRebook(
  item: ActivityItem,
  activeCustomerAccountId: string,
  evaluatedAtEpochMs = Date.now(),
): ActivityRebookResolution {
  const currentCatalogFreshness = fixtureFreshnessAt(
    evaluatedAtEpochMs,
    "fresh",
  );
  if (!currentCatalogFreshness) {
    return Object.freeze({
      status: "disabled" as const,
      reason: "catalog-unavailable" as const,
    });
  }
  return resolveActivityRebook({
    item,
    activeCustomerAccountId,
    catalog: {
      ...FRONTEND_ACTIVITY_REBOOK_CATALOG,
      freshness: currentCatalogFreshness,
    },
    evaluatedAtEpochMs,
  });
}

function money(amountMinor: number): ActivityMoney {
  return Object.freeze({ amountMinor, currency: "RON" });
}

function refund(
  status: "none" | "pending" | "completed" = "none",
): ActivityRefundEvidence {
  return status === "none"
    ? Object.freeze({
        status: "none",
        amount: null,
        providerReference: null,
        updatedAtEpochMs: null,
      })
    : Object.freeze({
        status,
        amount: money(5_000),
        providerReference: `refund-volera-${status}`,
        updatedAtEpochMs:
          FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS - 60 * 60 * 1_000,
      });
}

function payment(
  kind:
    | "free"
    | "pay-later"
    | "deposit"
    | "full-card"
    | "card-on-file"
    | "gift-card"
    | "split" = "pay-later",
  totalAmountMinor = 18_000,
): ActivityPaymentEvidence {
  if (kind === "free") {
    return Object.freeze({
      status: "not-required",
      total: money(0),
      paid: money(0),
      due: money(0),
      allocations: Object.freeze([]) as readonly [],
      deposit: null,
      cardOnFile: false,
    });
  }

  const total = money(totalAmountMinor);
  if (kind === "pay-later") {
    return Object.freeze({
      status: "pay-later",
      total,
      paid: money(0),
      due: total,
      allocations: Object.freeze([]),
      deposit: null,
      cardOnFile: false,
    });
  }

  if (kind === "deposit") {
    const deposit = money(5_000);
    return Object.freeze({
      status: "deposit-paid",
      total,
      paid: deposit,
      due: money(totalAmountMinor - 5_000),
      allocations: Object.freeze([
        Object.freeze({
          allocationId: "allocation-volera-deposit",
          kind: "card" as const,
          amount: deposit,
          displayHint: "•••• 4242",
        }),
      ]),
      deposit,
      cardOnFile: false,
    });
  }

  if (kind === "card-on-file") {
    return Object.freeze({
      status: "card-on-file",
      total,
      paid: money(0),
      due: total,
      allocations: Object.freeze([]),
      deposit: null,
      cardOnFile: true,
    });
  }

  if (kind === "gift-card") {
    return Object.freeze({
      status: "paid",
      total,
      paid: total,
      due: money(0),
      allocations: Object.freeze([
        Object.freeze({
          allocationId: "allocation-volera-gift-card",
          kind: "gift-card" as const,
          amount: total,
          displayHint: "Gift Card ••84",
        }),
      ]),
      deposit: null,
      cardOnFile: false,
    });
  }

  if (kind === "split") {
    return Object.freeze({
      status: "paid",
      total,
      paid: total,
      due: money(0),
      allocations: Object.freeze([
        Object.freeze({
          allocationId: "allocation-volera-gift-card-split",
          kind: "gift-card" as const,
          amount: money(5_000),
          displayHint: "Gift Card ••84",
        }),
        Object.freeze({
          allocationId: "allocation-volera-card-split",
          kind: "card" as const,
          amount: money(totalAmountMinor - 5_000),
          displayHint: "•••• 4242",
        }),
      ]),
      deposit: null,
      cardOnFile: false,
    });
  }

  return Object.freeze({
    status: "paid",
    total,
    paid: total,
    due: money(0),
    allocations: Object.freeze([
      Object.freeze({
        allocationId: "allocation-volera-card-full",
        kind: "card" as const,
        amount: total,
        displayHint: "•••• 4242",
      }),
    ]),
    deposit: null,
    cardOnFile: false,
  });
}

function pricingBreakdown(
  subtotalAmountMinor: number,
  paymentEvidence: ActivityPaymentEvidence,
  options: {
    adjustments?: ActivityPricingBreakdown["adjustments"];
    fees?: ActivityPricingBreakdown["fees"];
    taxAmountMinor?: number;
    tipAmountMinor?: number;
  } = {},
): ActivityPricingBreakdown {
  return Object.freeze({
    subtotal: money(subtotalAmountMinor),
    adjustments: Object.freeze(options.adjustments ?? []),
    fees: Object.freeze(options.fees ?? []),
    tax: money(options.taxAmountMinor ?? 0),
    tip: money(options.tipAmountMinor ?? 0),
    total: paymentEvidence.total,
    paid: paymentEvidence.paid,
    due: paymentEvidence.due,
  });
}

function destinationFor(
  action: ActivityActionKind,
  pointer: string,
): ActivityActionDestination {
  if (action === "open-details") {
    return Object.freeze({
      kind: "dialog",
      dialog: "activity-details",
      providerActionId: null,
      providerActionRevision: null,
    });
  }
  if (action === "cancel") {
    return Object.freeze({
      kind: "dialog",
      dialog: "cancel-appointment",
      providerActionId: `activity-cancel-${pointer.replace("~", "-")}`,
      providerActionRevision: "revision-1",
    });
  }
  if (action === "leave-waitlist") {
    return Object.freeze({
      kind: "dialog",
      dialog: "cancel-waitlist",
      providerActionId: `activity-leave-waitlist-${pointer.replace("~", "-")}`,
      providerActionRevision: "revision-1",
    });
  }
  if (action === "complete-form") {
    return Object.freeze({
      kind: "internal-route",
      path: buildCustomerFormAssignmentPath(
        CUSTOMER_FORMS_ACTIVITY_REQUIRED_FIXTURE.assignmentId,
      ),
    });
  }
  if (action === "directions") {
    return Object.freeze({
      kind: "external-url",
      url: `https://www.google.com/maps/dir/?api=1&destination=${LOCATION.latitude}%2C${LOCATION.longitude}`,
    });
  }
  if (action === "message-business") {
    return Object.freeze({
      kind: "internal-route",
      path: `/messages?conversation=${CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.messages.conversationId}`,
    });
  }
  if (action === "view-location") {
    return Object.freeze({
      kind: "internal-route",
      path: `/locations/${LOCATION.locationId}`,
    });
  }
  if (action === "rebook") {
    return Object.freeze({
      kind: "provider-handoff",
      handoffId: `activity-rebook-${pointer.replace("~", "-")}`,
      handoffRevision: "revision-1",
    });
  }
  if (action === "view-receipt") {
    return Object.freeze({
      kind: "provider-handoff",
      handoffId: `activity-receipt-${pointer.replace("~", "-")}`,
      handoffRevision: "revision-1",
    });
  }
  return Object.freeze({
    kind: "provider-handoff",
    handoffId: `handoff-${action}-${pointer.replace("~", "-")}`,
    handoffRevision: "revision-1",
  });
}

function allowedCapability(
  action: ActivityActionKind,
  pointer: string,
  options: {
    requiresFreshData?: boolean;
    requiresCompleteData?: boolean;
    destination?: ActivityActionDestination;
  } = {},
): ActivityActionCapability {
  return Object.freeze({
    action,
    availability: "allowed",
    destination: options.destination ?? destinationFor(action, pointer),
    reason: null,
    requiresFreshData: options.requiresFreshData ?? true,
    requiresCompleteData: options.requiresCompleteData ?? true,
  });
}

function blockedCapability(
  action: ActivityActionKind,
  reason: ActivityActionCapability["reason"] = "policy-blocked",
  availability: "disabled" | "hidden" = "disabled",
): ActivityActionCapability {
  return Object.freeze({
    action,
    availability,
    destination: null,
    reason,
    requiresFreshData: true,
    requiresCompleteData: true,
  });
}

function appointmentCapabilities(
  pointer: string,
  variant: "upcoming" | "past" | "blocked" | "receipt" = "upcoming",
): readonly ActivityActionCapability[] {
  const open = allowedCapability("open-details", pointer, {
    requiresFreshData: false,
    requiresCompleteData: false,
  });
  if (variant === "blocked") {
    return Object.freeze([
      open,
      blockedCapability("cancel"),
      blockedCapability("reschedule"),
      blockedCapability("rebook", "service-unavailable"),
      blockedCapability("review", "not-eligible"),
      blockedCapability("view-receipt", "not-published", "hidden"),
    ]);
  }
  if (variant === "past") {
    return Object.freeze([
      open,
      allowedCapability("directions", pointer, {
        requiresCompleteData: false,
      }),
      allowedCapability("rebook", pointer),
      allowedCapability("message-business", pointer, {
        requiresCompleteData: false,
      }),
      allowedCapability("view-location", pointer, {
        requiresCompleteData: false,
      }),
      allowedCapability("review", pointer),
      blockedCapability("cancel", "not-eligible", "hidden"),
      blockedCapability("reschedule", "not-eligible", "hidden"),
      blockedCapability("view-receipt", "not-published", "hidden"),
    ]);
  }
  if (variant === "receipt") {
    return Object.freeze([
      open,
      allowedCapability("rebook", pointer),
      allowedCapability("view-receipt", pointer),
    ]);
  }
  return Object.freeze([
    open,
    allowedCapability("add-to-calendar", pointer, {
      requiresCompleteData: false,
    }),
    allowedCapability("cancel", pointer),
    allowedCapability("reschedule", pointer),
    allowedCapability("directions", pointer, {
      requiresCompleteData: false,
    }),
    allowedCapability("message-business", pointer, {
      requiresCompleteData: false,
    }),
    allowedCapability("view-location", pointer, {
      requiresCompleteData: false,
    }),
    blockedCapability("review", "not-eligible", "hidden"),
  ]);
}

function serviceLine(
  input: {
    lineId?: string;
    serviceId?: string;
    name?: string;
    staff?: ActivityStaffSnapshot;
    addOns?: readonly ActivityAppointmentAddOn[];
  } = {},
): ActivityAppointmentLine {
  return Object.freeze({
    kind: "service",
    lineId: input.lineId ?? "line-volera-signature",
    serviceId:
      input.serviceId ?? CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.service.serviceId,
    name: input.name ?? CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.service.name,
    variantId: null,
    variantName: null,
    durationMinutes: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.service.durationMinutes,
    price: money(CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.service.priceAmountMinor),
    staff: input.staff ?? STAFF_MARA,
    addOns: Object.freeze(input.addOns ?? []),
  });
}

function membershipRedemptionAppointment(customerAccountId: string): AppointmentActivityItem {
  // Synthetic historical provider evidence only: never derived from a purchase or current Wallet balance.
  const first = serviceLine({ addOns: [{ groupId: "extras", addOnId: "extra-finish", name: "Finisare suplimentară", quantity: 1, durationMinutes: 5, price: money(500) }] });
  const second = { ...serviceLine({ lineId: "line-volera-brows", serviceId: "andrada-brow-shape", name: "Pensat și stilizare sprâncene" }), price: money(4_500) };
  const covered = first.price.amountMinor + second.price.amountMinor;
  const paymentEvidence = payment("pay-later", 500);
  const item = appointment("appointment-volera-membership-redemption", customerAccountId, {
    lines: [first, second], payment: paymentEvidence,
    pricing: pricingBreakdown(covered + 500, paymentEvidence, { adjustments: [{ adjustmentId: "membership-redemption-discount", kind: "membership-benefit", label: "Ședințe cu abonament", amount: money(covered) }] }),
  });
  const lines = [first, second].map((line, index) => ({ participantId: "synthetic-organizer", lineId: `synthetic-source-line-${index}`,
    serviceId: line.kind === "service" ? line.serviceId : "", variantId: null, entitlementId: `synthetic-entitlement-${index}`,
    membershipId: `synthetic-membership-${index}`, membershipVersion: "version-1", membershipName: index === 0 ? "Abonament lunar" : "Abonament nelimitat",
    targetId: `synthetic-target-${index}`, periodId: `synthetic-period-${index}`, periodRevision: "period-1", quotaRevision: "quota-1",
    sessionUnits: 1, coveredAmountMinor: line.price.amountMinor, payableAmountMinor: index === 0 ? 500 : 0 }));
  return { ...item, membershipRedemption: {
    schemaVersion: 1, kind: "activity-appointment-membership-redemption", activityId: item.activityId,
    entityRevision: item.revision.entityRevision, appointmentReference: item.appointmentReference, customerAccountId,
    locationId: item.location.locationId, providerBookingId: "synthetic-booking-memberships", providerReceiptId: "synthetic-receipt-memberships",
    publishedAtEpochMs: FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS,
    attribution: { schemaVersion: 1, kind: "membership-session-receipt-attribution", customerAccountId, locationId: item.location.locationId,
      providerId: FRONTEND_ACTIVITY_PROVIDER_ID, providerRevision: "membership-provider-1", providerBookingId: "synthetic-booking-memberships",
      providerReceiptId: "synthetic-receipt-memberships", appointmentDraftId: "synthetic-membership-draft", allocationId: "synthetic-allocation",
      allocationRevision: "allocation-1", acceptedAtEpochMs: FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS - 60_000,
      currency: "RON", totalCoveredAmountMinor: covered, lines },
    lineMappings: [first, second].map((line, index) => ({ activityLineId: line.lineId, sourceParticipantId: "synthetic-organizer", sourceLineId: `synthetic-source-line-${index}` })),
  } };
}

function bundleLine(): ActivityAppointmentLine {
  const children = Object.freeze([
    Object.freeze({
      childId: "andrada-brow-lamination-bundle-step",
      serviceId: "andrada-brow-lamination",
      name: "Laminare sprâncene",
      durationMinutes: 35,
      price: money(10_500),
      staff: STAFF_MARA,
      addOns: Object.freeze([]),
    }),
    Object.freeze({
      childId: "andrada-lash-lift-bundle-step",
      serviceId: "andrada-lash-lift",
      name: "Lifting gene",
      durationMinutes: 40,
      price: money(15_000),
      staff: STAFF_MARA,
      addOns: Object.freeze([]),
    }),
  ]) as unknown as readonly [
    {
      childId: string;
      serviceId: string;
      name: string;
      durationMinutes: number;
      price: ActivityMoney;
      staff: ActivityStaffSnapshot;
      addOns: readonly [];
    },
    ...{
      childId: string;
      serviceId: string;
      name: string;
      durationMinutes: number;
      price: ActivityMoney;
      staff: ActivityStaffSnapshot;
      addOns: readonly [];
    }[],
  ];
  return Object.freeze({
    kind: "bundle",
    lineId: "line-volera-bundle",
    bundleId: "andrada-brow-lash-package",
    name: "Pachet sprâncene și gene",
    schedule: "parallel",
    durationMinutes: 75,
    price: money(17_000),
    children,
  });
}

interface AppointmentFixtureOptions {
  status?: ActivityAppointmentStatus;
  group?: "upcoming" | "past";
  localDate?: string;
  localTime?: string;
  epoch?: number;
  lines?: readonly [ActivityAppointmentLine, ...ActivityAppointmentLine[]];
  party?: AppointmentActivityItem["party"];
  location?: ActivityLocationSnapshot;
  payment?: ActivityPaymentEvidence;
  pricing?: ActivityPricingBreakdown;
  refund?: ActivityRefundEvidence;
  forms?: readonly ActivityAppointmentFormAssignment[];
  attentionState?: ActivityAttentionState;
  cancellationPolicy?: AppointmentActivityItem["cancellationPolicy"];
  capabilities?: readonly ActivityActionCapability[];
  completeness?: ActivityCompleteness;
  freshness?: ActivityFreshness;
  ownershipRelationship?: "organizer" | "participant";
}

function appointment(
  id: string,
  customerAccountId: string,
  options: AppointmentFixtureOptions = {},
): AppointmentActivityItem {
  const pointer = createActivityPointer("appointment", id);
  if (!pointer) {
    throw new Error("Invalid synthetic Activity fixture ID");
  }

  const lines =
    options.lines ??
    (Object.freeze([serviceLine()]) as readonly [
      ActivityAppointmentLine,
      ...ActivityAppointmentLine[],
    ]);
  const group = options.group ?? "upcoming";
  const occurredAtEpochMs =
    options.epoch ??
    (group === "upcoming" ? Date.UTC(2026, 8, 1, 12) : Date.UTC(2026, 6, 4, 8));
  const localDateTime = fixtureLocalDateTime(occurredAtEpochMs);
  const configuredTotal = lines.reduce((total, line) => {
    const addOnTotal =
      line.kind === "service"
        ? line.addOns.reduce(
            (sum, addOn) => sum + (addOn.price?.amountMinor ?? 0) * addOn.quantity,
            0,
          )
        : line.children.reduce(
            (sum, child) =>
              sum +
              child.addOns.reduce(
                (childSum, addOn) =>
                  childSum + (addOn.price?.amountMinor ?? 0) * addOn.quantity,
                0,
              ),
            0,
          );

    return total + line.price.amountMinor + addOnTotal;
  }, 0);
  const resolvedPayment =
    options.payment ?? payment("pay-later", configuredTotal);
  const resolvedPricing =
    options.pricing ?? pricingBreakdown(configuredTotal, resolvedPayment);
  return {
    schemaVersion: ACTIVITY_SCHEMA_VERSION,
    pointer,
    activityType: "appointment",
    activityId: id,
    tab: "appointments",
    group,
    ownership: {
      customerAccountId,
      relationship: options.ownershipRelationship ?? "organizer",
    },
    business: BUSINESS,
    location: options.location ?? LOCATION,
    previewMedia: PREVIEW_MEDIA,
    status: options.status ?? "confirmed",
    attentionState: options.attentionState ?? NO_ATTENTION,
    temporal: {
      timeZone: FIXTURE_TIME_ZONE,
      occurredAtEpochMs,
      localDate: options.localDate ?? localDateTime.localDate,
      localTime: options.localTime ?? localDateTime.localTime,
      durationMinutes: Math.max(...lines.map((line) => line.durationMinutes)),
    },
    payment: resolvedPayment,
    refund: options.refund ?? refund(),
    cancellationPolicy: options.cancellationPolicy ?? {
      availability: "available",
      policyId: "policy-volera-free",
      policyRevision: "revision-1",
      kind: "free",
      fee: null,
      freeUntilEpochMs: occurredAtEpochMs - 24 * 60 * 60 * 1_000,
      providerCopy: "Anularea este gratuită înainte de termenul publicat.",
    },
    capabilities:
      options.capabilities ??
      appointmentCapabilities(
        pointer,
        group === "upcoming" ? "upcoming" : "past",
      ),
    revision: {
      dataRevision: FRONTEND_ACTIVITY_DATA_REVISION,
      entityRevision: `revision-${id}`,
      generatedAtEpochMs: FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS,
    },
    completeness: options.completeness ?? COMPLETE,
    freshness: options.freshness ?? FRESH,
    appointmentReference: `VOL-${id.toUpperCase()}`,
    lines,
    pricing: resolvedPricing,
    forms: options.forms ?? [],
    party:
      options.party ??
      ({
        kind: "individual",
        groupId: null,
        customerRole: "organizer",
        participants: [],
      } as const),
    notes: null,
  };
}

/** The development provider publishes a new Activity row only from its exact
 * durable accepted atomic booking. Current balances never recreate history.
 * Unpublished cancellation, reschedule, refund and invoice authority stays
 * disabled; acceptance alone is not permission to mutate the appointment. */
export function createFrontendPackageAtomicAppointmentActivity(input: {
  readonly row: unknown; readonly customerAccountId: string; readonly observedAtEpochMs: number;
}): AppointmentActivityItem | null {
  try {
    const row = validateFrontendPackageAtomicRowV3(input.row);
    if (!row || row.state !== "consumed" || row.terminal?.response.status !== "accepted" ||
      row.operation.customerAccountId !== input.customerAccountId || !Number.isSafeInteger(input.observedAtEpochMs)) return null;
    const receipt = row.terminal.response.receipt;
    const proof = row.terminal.response.packageSessionAtomicEvidence.request.packageSessionProof;
    if (proof?.schemaVersion !== 3) return null;
    const resolved = resolvePackageSessionReceiptAttributionV1({ receipt, terminalProof: proof });
    if (resolved.status !== "ready" || receipt.createdAtEpochMs > input.observedAtEpochMs) return null;
    const { attribution } = resolved; const context = proof.selection.allocation.request.context;
    const scheduling = receipt.reviewDraft.intakeDraft.schedulingDraft;
    if (context.catalog.locationId !== LOCATION.locationId || scheduling.appointmentDraft.timeZone !== FIXTURE_TIME_ZONE || attribution.currency !== "RON" ||
      scheduling.appointmentDraft.participants.length !== 1 || !isActivityIdentifier(receipt.providerBookingId)) return null;
    const participant = scheduling.appointmentDraft.participants[0];
    const planned = buildAppointmentSchedulePlan(scheduling.appointmentDraft); if (!planned.ok) return null;
    const lines: ActivityAppointmentLine[] = [];
    for (const quote of context.quote.lines) {
      const line = participant.serviceLines.find(value => value.lineId === quote.lineId);
      if (participant.participantId !== quote.participantId || !line || line.selection.kind !== "service" || line.selection.serviceId !== quote.serviceId ||
        line.selection.variantId !== quote.variantId) return null;
      const tasks = planned.plan.tasks.filter(task => task.participantId === participant.participantId && task.lineId === line.lineId);
      const staffIds = [...new Set(tasks.map(task => scheduling.selectedSlot.taskAllocations.find(value => value.taskId === task.taskId)?.staffUserLocationId))];
      if (staffIds.length !== 1 || !staffIds[0]) return null;
      const staff = receipt.presentationSnapshot.professionals?.find(value => value.userLocationId === staffIds[0]);
      if (!staff) return null;
      const addOns: ActivityAppointmentAddOn[] = [];
      for (const quoted of quote.addOns) {
        const snapshot = line.snapshot.addOns.find(value => value.bundleItemId === null && value.groupId === quoted.groupId && value.addOnId === quoted.addOnId);
        if (!snapshot || snapshot.quantity !== quoted.quantity) return null;
        addOns.push({ addOnId: quoted.addOnId, groupId: quoted.groupId, name: snapshot.name, quantity: quoted.quantity,
          durationMinutes: null, price: money(quoted.unitAmountMinor) });
      }
      lines.push({ kind: "service", lineId: line.lineId, serviceId: quote.serviceId, name: line.snapshot.serviceName,
        variantId: quote.variantId, variantName: line.snapshot.variantName, durationMinutes: line.quote.durationMinutes,
        price: money(quote.baseAmountMinor), staff: { resolution: "specific", userLocationId: staff.userLocationId, displayName: staff.name, imageUrl: null, availability: "available" }, addOns });
    }
    if (lines.length === 0 || lines.length !== participant.serviceLines.length) return null;
    const total = attribution.totalPayableAmountMinor; const paid = receipt.payment?.authorizedAmountMinor ?? 0;
    const boundary = receipt.reviewDraft.intakeDraft.completionPolicy.paymentBoundary;
    const cardOnFile = receipt.payment !== null && boundary.kind === "appointment-protection" && boundary.requirement === "card-on-file";
    const deposit = receipt.payment !== null && paid > 0 && paid < total && boundary.kind === "appointment-protection" && boundary.requirement === "deposit";
    const paymentEvidence: ActivityPaymentEvidence = total === 0 && !cardOnFile ? payment("free") : { status: cardOnFile && paid === 0 ? "card-on-file" : paid === 0 ? "pay-later" : paid === total ? "paid" : deposit ? "deposit-paid" : "partially-paid",
      total: money(total), paid: money(paid), due: money(total - paid), cardOnFile, deposit: deposit ? money(paid) : null,
      allocations: (receipt.payment?.allocations ?? []).filter(value => value.amountMinor > 0).map((value, index) => ({
        allocationId: `receipt-allocation-${index}`, kind: "other", amount: money(value.amountMinor), displayHint: null })) };
    const adjustments: ActivityPricingBreakdown["adjustments"][number][] = [];
    if (attribution.totalCoveredAmountMinor + attribution.totalPackageDiscountAmountMinor > 0) adjustments.push({ adjustmentId: "exact-package-allocation",
      kind: "package-benefit", label: receipt.reviewDraft.benefitEligibility.eligibleBenefits.find(value => value.benefitId === receipt.reviewDraft.appliedBenefitId)?.titleSnapshot ?? "Package benefits",
      amount: money(attribution.totalCoveredAmountMinor + attribution.totalPackageDiscountAmountMinor) });
    for (const adjustment of receipt.reviewDraft.pricingAdjustments?.automaticDeals ?? []) adjustments.push({ adjustmentId: adjustment.dealId, kind: "discount", label: adjustment.titleSnapshot, amount: money(adjustment.discountAmountMinor) });
    const promotion = receipt.reviewDraft.pricingAdjustments?.promotionCodeClaim;
    if (promotion) adjustments.push({ adjustmentId: promotion.claimId, kind: "promotion", label: promotion.titleSnapshot, amount: money(promotion.discountAmountMinor) });
    if (receipt.reviewDraft.pricingAdjustments?.serviceBenefitsV1) {
      const evidence = resolveServiceBenefitsReviewEvidence(receipt.reviewDraft);
      if (!evidence) return null;
      adjustments.push(...projectServiceBenefitsRewardAdjustmentRows(evidence).map(row => ({ adjustmentId: row.adjustmentId, kind: "discount" as const, label: row.label, amount: money(row.amountMinor) })));
    }
    const id = receipt.providerBookingId; const pointer = createActivityPointer("appointment", id); if (!pointer) return null;
    const item: AppointmentActivityItem = { ...appointment(id, input.customerAccountId, { status: "confirmed",
      group: scheduling.selectedSlot.endsAtEpochMs > input.observedAtEpochMs ? "upcoming" : "past", epoch: scheduling.selectedSlot.startsAtEpochMs,
      localDate: scheduling.selectedSlot.localStartDate, localTime: scheduling.selectedSlot.localStartTime,
      lines: lines as [ActivityAppointmentLine, ...ActivityAppointmentLine[]], payment: paymentEvidence,
      pricing: pricingBreakdown(attribution.totalOriginalAmountMinor, paymentEvidence, { adjustments }), capabilities: appointmentCapabilities(pointer, "blocked"),
      cancellationPolicy: { availability: "unavailable", policyId: null, policyRevision: null, kind: null, fee: null, freeUntilEpochMs: null, providerCopy: null },
    }), appointmentReference: receipt.providerBookingId, notes: receipt.reviewDraft.customerComment,
      revision: { dataRevision: FRONTEND_ACTIVITY_DATA_REVISION, entityRevision: `receipt-${receipt.createdAtEpochMs}`, generatedAtEpochMs: input.observedAtEpochMs },
      freshness: { status: "fresh", observedAtEpochMs: input.observedAtEpochMs, staleAtEpochMs: input.observedAtEpochMs + 30 * 60_000, reason: null } };
    item.temporal.durationMinutes = (scheduling.selectedSlot.endsAtEpochMs - scheduling.selectedSlot.startsAtEpochMs) / 60_000;
    const packageRedemption = createActivityAppointmentPackageRedemptionV1({ item, attribution, publishedAtEpochMs: input.observedAtEpochMs,
      lineMappings: context.quote.lines.map(line => ({ activityLineId: line.lineId, sourceParticipantId: line.participantId, sourceLineId: line.lineId })) });
    if (!packageRedemption) return null;
    const validated = validateActivityItem({ ...item, packageRedemption });
    return validated.valid && validated.item.activityType === "appointment" ? validated.item : null;
  } catch { return null; }
}

function packageSessionAppointment(
  customerAccountId: string,
): AppointmentActivityItem {
  const line = serviceLine({
    lineId: "line-volera-package-session-signature",
  });
  const packagePayment = payment("free");

  return {
    ...appointment(
      PACKAGE_SESSION_APPOINTMENT_FIXTURE.activityId,
      customerAccountId,
      {
        status: "confirmed",
        group: "upcoming",
        epoch: PACKAGE_SESSION_APPOINTMENT_FIXTURE.startsAtEpochMs,
        lines: [line],
        payment: packagePayment,
        pricing: pricingBreakdown(line.price.amountMinor, packagePayment, {
          adjustments: [
            {
              adjustmentId: "adjustment-volera-package-session-1",
              kind: "package-benefit",
              label: "Sesiune pachet",
              amount: line.price,
            },
          ],
        }),
        attentionState: NO_ATTENTION,
        forms: [],
      },
    ),
    appointmentReference: PACKAGE_SESSION_APPOINTMENT_FIXTURE.reference,
  };
}

function waitlist(
  customerAccountId: string,
  status: "active" | "offered" | "expired" = "active",
): WaitlistActivityItem {
  const activityId =
    status === "active" ? "waitlist-volera-1" : `waitlist-volera-${status}`;
  const pointer = createActivityPointer("waitlist", activityId)!;
  const serviceLines = Object.freeze([
    Object.freeze({
      lineId: "waitlist-line-volera-signature",
      serviceId: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.service.serviceId,
      name: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.service.name,
      variantId: null,
      variantName: null,
      durationMinutes: 75,
      price: money(18_000),
      staff: STAFF_ANY,
      addOns: Object.freeze([
        Object.freeze({
          addOnId: "premium-skin-prep",
          groupId: "andrada-makeup-extras",
          name: "Pregătire premium a tenului",
          quantity: 1,
          durationMinutes: 15,
          price: money(3_500),
        }),
      ]),
    }),
  ]) as WaitlistActivityItem["serviceLines"];
  const preferences = Object.freeze([
    Object.freeze({
      preferenceId: "waitlist-preference-volera-1",
      localDate: "2026-09-01",
      startLocalTime: "09:00",
      endLocalTime: "12:00",
    }),
    Object.freeze({
      preferenceId: "waitlist-preference-volera-2",
      localDate: "2026-09-02",
      startLocalTime: null,
      endLocalTime: null,
    }),
  ]) as WaitlistActivityItem["preferences"];
  const quotedPayment = payment("pay-later", 21_500);
  const offerDestination = Object.freeze({
    kind: "provider-handoff" as const,
    handoffId: "waitlist-offer-volera-1",
    handoffRevision: "revision-1",
  });
  const offeredTemporal = Object.freeze({
    timeZone: FIXTURE_TIME_ZONE,
    occurredAtEpochMs: Date.UTC(2026, 8, 1, 7),
    localDate: "2026-09-01",
    localTime: "10:00",
    durationMinutes: 75,
  });
  const offeredSlot: WaitlistActivityItem["offeredSlot"] =
    status === "active"
      ? Object.freeze({
          status: "none",
          offerId: null,
          offerRevision: null,
          offeredAtEpochMs: null,
          expiresAtEpochMs: null,
          slotId: null,
          slotRevision: null,
          slot: null,
          destination: null,
        })
      : status === "offered"
        ? Object.freeze({
            status: "available",
            offerId: "offer-volera-1",
            offerRevision: "revision-1",
            offeredAtEpochMs: Date.UTC(2026, 7, 31, 8),
            expiresAtEpochMs: Date.UTC(2026, 7, 31, 10),
            slotId: "slot-volera-waitlist-1",
            slotRevision: "slot-revision-1",
            slot: offeredTemporal,
            destination: offerDestination,
          })
        : Object.freeze({
            status: "expired",
            offerId: "offer-volera-1",
            offerRevision: "revision-1",
            offeredAtEpochMs: Date.UTC(2026, 7, 31, 8),
            expiresAtEpochMs: Date.UTC(2026, 7, 31, 10),
            slotId: "slot-volera-waitlist-1",
            slotRevision: "slot-revision-1",
            slot: offeredTemporal,
            destination: null,
          });
  const capabilities: ActivityActionCapability[] = [
    allowedCapability("open-details", pointer, {
      requiresFreshData: false,
      requiresCompleteData: false,
    }),
    allowedCapability("view-location", pointer, {
      requiresFreshData: false,
      requiresCompleteData: false,
      destination: Object.freeze({
        kind: "internal-route",
        path: `/locations/${LOCATION.locationId}`,
      }),
    }),
  ];
  if (status !== "expired") {
    capabilities.push(allowedCapability("leave-waitlist", pointer));
  }
  if (status === "offered") {
    capabilities.push(
      allowedCapability("book-offered-slot", pointer, {
        destination: offerDestination,
      }),
    );
  }
  return {
    schemaVersion: ACTIVITY_SCHEMA_VERSION,
    pointer,
    activityType: "waitlist",
    activityId,
    tab: "appointments",
    group: "waitlist",
    ownership: {
      customerAccountId,
      relationship: "organizer",
    },
    business: BUSINESS,
    location: LOCATION,
    previewMedia: PREVIEW_MEDIA,
    status,
    attentionState:
      status === "offered"
        ? {
            status: "action-required",
            reason: "confirm-offered-slot",
            dueAtEpochMs: Date.UTC(2026, 7, 31, 10),
          }
        : status === "active"
          ? {
              status: "informational",
              reason: "waitlist-availability",
              dueAtEpochMs: null,
            }
          : NO_ATTENTION,
    temporal: {
      timeZone: FIXTURE_TIME_ZONE,
      occurredAtEpochMs: Date.UTC(2026, 7, 30, 8),
      localDate: "2026-08-30",
      localTime: "11:00",
      durationMinutes: null,
    },
    payment: quotedPayment,
    refund: refund(),
    cancellationPolicy: {
      availability: "unavailable",
      policyId: null,
      policyRevision: null,
      kind: null,
      fee: null,
      freeUntilEpochMs: null,
      providerCopy: null,
    },
    capabilities,
    revision: {
      dataRevision: FRONTEND_ACTIVITY_DATA_REVISION,
      entityRevision: "revision-waitlist-volera-1",
      generatedAtEpochMs: FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS,
    },
    completeness: COMPLETE,
    freshness: FRESH,
    waitlistReference: "WAIT-VOL-001",
    serviceLines,
    preferences,
    offeredSlot,
    pricing: pricingBreakdown(21_500, quotedPayment),
    serviceName: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.service.name,
    preferredDates: ["2026-09-01", "2026-09-02"],
    staff: STAFF_ANY,
  };
}

function commerceBase(
  type: "gift-card" | "membership" | "product" | "package",
  id: string,
  customerAccountId: string,
) {
  const pointer = createActivityPointer(type, id)!;
  return {
    schemaVersion: ACTIVITY_SCHEMA_VERSION,
    pointer,
    activityType: type,
    activityId: id,
    ownership: {
      customerAccountId,
      relationship:
        type === "membership" ? ("member" as const) : ("purchaser" as const),
    },
    business: BUSINESS,
    location: LOCATION,
    previewMedia: PREVIEW_MEDIA,
    attentionState: NO_ATTENTION,
    temporal: {
      timeZone: FIXTURE_TIME_ZONE,
      occurredAtEpochMs: Date.UTC(2026, 6, 15, 9),
      localDate: "2026-07-15",
      localTime: "12:00",
      durationMinutes: null,
    },
    payment: payment("full-card"),
    refund: refund(),
    cancellationPolicy: {
      availability: "unavailable" as const,
      policyId: null,
      policyRevision: null,
      kind: null,
      fee: null,
      freeUntilEpochMs: null,
      providerCopy: null,
    },
    capabilities: [
      allowedCapability("open-details", pointer, {
        requiresFreshData: false,
        requiresCompleteData: false,
      }),
    ],
    revision: {
      dataRevision: FRONTEND_ACTIVITY_DATA_REVISION,
      entityRevision: `revision-${id}`,
      generatedAtEpochMs: FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS,
    },
    completeness: COMPLETE,
    freshness: FRESH,
  };
}

function activityPackageMatchesOwned(item: PackageActivityItem, owned: PackageOwnedSnapshotV2) {
  return item.ownership.customerAccountId === owned.customerAccountId && item.pointer === owned.activityPointer &&
    item.entitlementId === owned.entitlementId && item.soldPackageVersion === owned.soldPackageVersion &&
    item.packageId === owned.definition.packageId && item.packageVersion === owned.definition.packageVersion &&
    item.location.locationId === owned.definition.locationId && item.purchasedAtEpochMs === owned.purchasedAtEpochMs;
}

function currentOwnedPackageActivity(owned: PackageOwnedSnapshotV2, legacy: PackageActivityItem | undefined, freshness: ActivityFreshness): PackageActivityItem | null {
  if (owned.definition.locationId !== LOCATION.locationId || legacy && !activityPackageMatchesOwned(legacy, owned)) return null;
  const parsed = parseActivityPointer(owned.activityPointer);
  if (parsed?.activityType !== "package") return null;
  const sale = readFrontendPackagePurchaseSaleV2({ draftId: owned.purchaseDraftId, customerAccountId: owned.customerAccountId });
  // Checkout-only seed inventory has no historical finance publication.
  const giftClaim = readFrontendPackageGiftClaimForOwned(owned);
  if (!legacy && sale.status !== "ready" && !giftClaim) return null;
  let financial = legacy?.payment;
  if (giftClaim?.origin === "demo") {
    const zero = { amountMinor: 0, currency: owned.definition.price.currency };
    financial = { status: "not-required", total: zero, paid: zero, due: zero, allocations: [], deposit: null, cardOnFile: false };
  }
  if (sale.status === "ready") {
    const receipt = sale.receipt; const quote = receipt.checkout.quote; const commerce = receipt.checkout.commerce;
    const tender = receipt.checkout.paymentMethod;
    const total = { amountMinor: quote.totalAmountMinor, currency: quote.currency };
    const allocations: ActivityPaymentEvidence["allocations"] = [
      ...(commerce?.tenders.allocations.map(row => ({ allocationId: `gift-${row.giftCardId}`, kind: "gift-card" as const,
        amount: { amountMinor: row.amountMinor, currency: quote.currency }, displayHint: commerce.tenders.inventory.find(card => card.giftCardId === row.giftCardId)?.maskedDisplay ?? null })) ?? []),
      ...(tender && (commerce?.tenders.remainderMinor ?? quote.totalAmountMinor) > 0 ? [{ allocationId: receipt.paymentReferenceId!, kind: tender.kind,
        amount: { amountMinor: commerce?.tenders.remainderMinor ?? quote.totalAmountMinor, currency: quote.currency }, displayHint: tender.label }] : []),
    ];
    financial = quote.totalAmountMinor === 0 ? { status: "not-required", total, paid: total, due: total, allocations: [], deposit: null, cardOnFile: false }
      : { status: "paid", total, paid: total, due: { amountMinor: 0, currency: quote.currency }, allocations, deposit: null, cardOnFile: false };
  }
  if (!financial) return null;
  const latest = owned.events[0]; const local = fixtureLocalDateTime(latest.occurredAtEpochMs);
  const item: PackageActivityItem = { ...commerceBase("package", parsed.activityId, owned.customerAccountId),
    activityType: "package", tab: "packages", group: "packages", status: owned.status,
    entitlementId: owned.entitlementId, soldPackageVersion: owned.soldPackageVersion, packageId: owned.definition.packageId,
    packageVersion: owned.definition.packageVersion, packageReference: owned.providerOrderId, name: owned.definition.name,
    purchasedAtEpochMs: owned.purchasedAtEpochMs, expiresAtEpochMs: owned.expiresAtEpochMs, benefits: [], ownedPackageV2: owned,
    ...(legacy ? { linkedAppointments: legacy.linkedAppointments ?? legacy.benefits.flatMap(benefit => benefit.balance.liveReservations) } : {}),
    events: owned.events, payment: financial, freshness,
    temporal: { timeZone: FIXTURE_TIME_ZONE, occurredAtEpochMs: latest.occurredAtEpochMs, localDate: local.localDate, localTime: local.localTime, durationMinutes: null },
    revision: { dataRevision: FRONTEND_ACTIVITY_DATA_REVISION, entityRevision: owned.revision, generatedAtEpochMs: Math.max(owned.updatedAtEpochMs, freshness.observedAtEpochMs) },
    invoice: legacy?.invoice ?? { availability: "unavailable", invoiceId: null, invoiceReference: null, invoiceRevision: null, issuedAtEpochMs: null, total: null },
  };
  return validateActivityItem(item).valid ? item : null;
}

function currentOwnedGiftCardActivity(publication: FrontendGiftCardCurrentPresentationV1, freshness: ActivityFreshness): GiftCardActivityItem {
  const { detail, recipient, activityStatus } = publication;
  const card = detail.card;
  const pointer = createActivityPointer("gift-card", card.giftCardId)!;
  const latest = detail.activity[detail.activity.length - 1];
  const local = fixtureLocalDateTime(latest.occurredAtEpochMs, publication.timeZone);
  const handoff = (action: "print-gift-card" | "copy-gift-card-code" | "view-invoice") =>
    allowedCapability(action, pointer, { destination: { kind: "provider-handoff", handoffId: `current-${action}-${card.giftCardId}`, handoffRevision: detail.detailRevision } });
  const history = detail.purchaseHistory;
  const paid = history ? { amountMinor: history.totalAmountMinor, currency: history.currency } : null;
  const purchasePayment: ActivityPaymentEvidence = history && paid && history.paymentMethod.kind !== "no-payment"
    ? { status: "paid", total: paid, paid, due: { ...paid, amountMinor: 0 }, deposit: null, cardOnFile: false,
        allocations: [{ allocationId: history.providerOrderId, kind: history.paymentMethod.kind, amount: paid, displayHint: history.paymentMethod.label }] }
    : { status: "not-required", total: { amountMinor: 0, currency: card.currency }, paid: { amountMinor: 0, currency: card.currency },
        due: { amountMinor: 0, currency: card.currency }, allocations: [], deposit: null, cardOnFile: false };
  return {
    ...commerceBase("gift-card", card.giftCardId, card.customerAccountId), activityType: "gift-card", tab: "gift-cards", group: "gift-cards",
    business: { businessId: card.businessId, name: card.businessName, imageUrl: null },
    previewMedia: [PREVIEW_MEDIA[0]],
    ownership: { customerAccountId: card.customerAccountId, relationship: "purchaser" }, status: activityStatus,
    temporal: { timeZone: publication.timeZone, occurredAtEpochMs: latest.occurredAtEpochMs, localDate: local.localDate, localTime: local.localTime, durationMinutes: null },
    revision: { dataRevision: FRONTEND_ACTIVITY_DATA_REVISION, entityRevision: detail.detailRevision, generatedAtEpochMs: publication.publishedAtEpochMs },
    freshness, detail, recipient, giftCardReference: card.giftCardId, maskedCode: card.maskedCode,
    originalValue: { amountMinor: card.faceValueAmountMinor, currency: card.currency }, balance: { amountMinor: card.remainingBalanceAmountMinor, currency: card.currency },
    expiresAtEpochMs: card.expiresAtEpochMs, payment: history ? purchasePayment : payment("free"),
    capabilities: [allowedCapability("open-details", pointer, { requiresFreshData: false, requiresCompleteData: false }),
      ...(detail.actions.canShare ? [allowedCapability("share", pointer, { destination: { kind: "dialog", dialog: "share-gift-card", providerActionId: null, providerActionRevision: null } }), handoff("copy-gift-card-code")] : []),
      ...(detail.actions.canPrint ? [handoff("print-gift-card")] : []), ...(detail.actions.canViewInvoice ? [handoff("view-invoice")] : []),
      allowedCapability("view-location", pointer, { requiresCompleteData: false })],
  };
}

function giftCard(
  customerAccountId: string,
  variant: "shared-unclaimed" | "depleted",
): GiftCardActivityItem {
  const giftCardId = `gift-card-volera-${variant}`;
  const pointer = createActivityPointer("gift-card", giftCardId)!;
  const faceValueAmountMinor = variant === "shared-unclaimed" ? 30_000 : 25_000;
  const remainingBalanceAmountMinor =
    variant === "shared-unclaimed" ? 30_000 : 0;
  const detailRevision = `gift-card-detail-${variant}-v1`;
  const purchasedAtEpochMs =
    variant === "shared-unclaimed"
      ? Date.UTC(2026, 7, 1, 9)
      : Date.UTC(2026, 6, 1, 9);
  const activity = (
    variant === "shared-unclaimed"
      ? [
          {
            activityId: "gift-card-event-shared-purchased",
            kind: "purchased" as const,
            occurredAtEpochMs: purchasedAtEpochMs,
            amountMinor: faceValueAmountMinor,
            balanceAfterAmountMinor: faceValueAmountMinor,
          },
          {
            activityId: "gift-card-event-shared-delivered",
            kind: "shared" as const,
            occurredAtEpochMs: Date.UTC(2026, 7, 8, 9),
            amountMinor: null,
            balanceAfterAmountMinor: faceValueAmountMinor,
          },
        ]
      : [
          {
            activityId: "gift-card-event-depleted-purchased",
            kind: "purchased" as const,
            occurredAtEpochMs: purchasedAtEpochMs,
            amountMinor: faceValueAmountMinor,
            balanceAfterAmountMinor: faceValueAmountMinor,
          },
          {
            activityId: "gift-card-event-depleted-shared",
            kind: "shared" as const,
            occurredAtEpochMs: Date.UTC(2026, 6, 2, 9),
            amountMinor: null,
            balanceAfterAmountMinor: faceValueAmountMinor,
          },
          {
            activityId: "gift-card-event-depleted-claimed",
            kind: "claimed" as const,
            occurredAtEpochMs: Date.UTC(2026, 6, 3, 9),
            amountMinor: null,
            balanceAfterAmountMinor: faceValueAmountMinor,
          },
          {
            activityId: "gift-card-event-depleted-redemption-one",
            kind: "redeemed" as const,
            occurredAtEpochMs: Date.UTC(2026, 6, 15, 9),
            amountMinor: 10_000,
            balanceAfterAmountMinor: 15_000,
          },
          {
            activityId: "gift-card-event-depleted-redemption-two",
            kind: "redeemed" as const,
            occurredAtEpochMs: Date.UTC(2026, 7, 3, 9),
            amountMinor: 15_000,
            balanceAfterAmountMinor: 0,
          },
        ]
  ) satisfies readonly ClientGiftCardActivity[];
  const latestEvent = activity[activity.length - 1];
  const localDateTime = fixtureLocalDateTime(latestEvent.occurredAtEpochMs);
  const canShare = variant === "shared-unclaimed";
  const canPrint = true;
  const detail: ClientGiftCardDetail = {
    customerAccountId,
    detailRevision,
    card: {
      customerAccountId,
      cardRevision: `gift-card-card-${variant}-v1`,
      giftCardId,
      businessId: BUSINESS.businessId,
      eligibleLocationIds: [LOCATION.locationId],
      businessName: BUSINESS.name,
      maskedCode:
        variant === "shared-unclaimed" ? "•••• •••• 7312" : "•••• •••• 2484",
      status: variant === "shared-unclaimed" ? "shared" : "active",
      faceValueAmountMinor,
      remainingBalanceAmountMinor,
      currency: "RON",
      expiresAtEpochMs:
        variant === "shared-unclaimed" ? Date.UTC(2027, 7, 8, 9) : null,
    },
    activity,
    actions: {
      canShare,
      shareConfiguration: canShare
        ? {
            sharePolicyId: "gift-card-share-shared-unclaimed",
            version: "v1",
            timeZone: FIXTURE_TIME_ZONE,
            minimumSendDate: "2026-08-31",
            maximumSendDate: "2027-08-31",
            maximumNameLength: 100,
            maximumMessageLength: 150,
            defaultStyleId: "gift-card-style-blush",
            styles: [
              {
                styleId: "gift-card-style-blush",
                name: "Blush",
                theme: "blush",
              },
              {
                styleId: "gift-card-style-midnight",
                name: "Midnight",
                theme: "midnight",
              },
            ],
          }
        : null,
      canPrint,
      canViewInvoice: true,
    },
  };
  const readOnlyDestination = (
    action: "print-gift-card" | "copy-gift-card-code" | "view-invoice",
  ): ActivityActionDestination => ({
    kind: "provider-handoff",
    handoffId: `activity-${action}-${giftCardId}`,
    handoffRevision: detailRevision,
  });
  const capabilities: ActivityActionCapability[] = [
    allowedCapability("open-details", pointer, {
      requiresFreshData: false,
      requiresCompleteData: false,
    }),
    allowedCapability("view-invoice", pointer, {
      destination: readOnlyDestination("view-invoice"),
    }),
    allowedCapability("view-location", pointer, {
      requiresCompleteData: false,
    }),
  ];
  capabilities.splice(
    1,
    0,
    allowedCapability("print-gift-card", pointer, {
      destination: readOnlyDestination("print-gift-card"),
    }),
  );
  if (canShare) {
    capabilities.splice(
      1,
      0,
      allowedCapability("share", pointer, {
        destination: {
          kind: "dialog",
          dialog: "share-gift-card",
          providerActionId: null,
          providerActionRevision: null,
        },
      }),
    );
    capabilities.splice(
      3,
      0,
      allowedCapability("copy-gift-card-code", pointer, {
        destination: readOnlyDestination("copy-gift-card-code"),
      }),
    );
  }

  return {
    ...commerceBase("gift-card", giftCardId, customerAccountId),
    activityType: "gift-card",
    tab: "gift-cards",
    group: "gift-cards",
    ownership: {
      customerAccountId,
      relationship: variant === "shared-unclaimed" ? "purchaser" : "recipient",
    },
    status: variant,
    temporal: {
      timeZone: FIXTURE_TIME_ZONE,
      occurredAtEpochMs: latestEvent.occurredAtEpochMs,
      localDate: localDateTime.localDate,
      localTime: localDateTime.localTime,
      durationMinutes: null,
    },
    payment: payment("free"),
    capabilities: Object.freeze(capabilities),
    revision: {
      dataRevision: FRONTEND_ACTIVITY_DATA_REVISION,
      entityRevision: detailRevision,
      generatedAtEpochMs: FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS,
    },
    detail,
    recipient: {
      displayName: "Ana",
      claimState: variant === "shared-unclaimed" ? "unclaimed" : "claimed",
      deliveryChannel: "email",
    },
    giftCardReference: detail.card.giftCardId,
    maskedCode: detail.card.maskedCode,
    originalValue: money(detail.card.faceValueAmountMinor),
    balance: money(detail.card.remainingBalanceAmountMinor),
    expiresAtEpochMs: detail.card.expiresAtEpochMs,
  };
}

function giftCards(customerAccountId: string): readonly GiftCardActivityItem[] {
  return Object.freeze([
    giftCard(customerAccountId, "shared-unclaimed"),
    giftCard(customerAccountId, "depleted"),
  ]);
}

export const FRONTEND_MEMBERSHIP_PURCHASE_FIXTURE = Object.freeze({
  purchasedAtEpochMs: Date.UTC(2026, 5, 15, 9),
  amountMinor: 14_900,
  currency: "RON",
  membershipName: "Abonament Volera Lunar",
  invoiceRevision: "membership-invoice-v1",
});

/** Original sold names for the separately operated development scenarios. */
export const FRONTEND_MEMBERSHIP_SCENARIO_PURCHASE_FIXTURES = Object.freeze({
  "one-session": Object.freeze({ ...FRONTEND_MEMBERSHIP_PURCHASE_FIXTURE, membershipName: "Abonament Volera · 1 ședință" }),
  unlimited: Object.freeze({ ...FRONTEND_MEMBERSHIP_PURCHASE_FIXTURE, membershipName: "Abonament Volera · Nelimitat" }),
  "fixed-no-early-cancel": Object.freeze({ ...FRONTEND_MEMBERSHIP_PURCHASE_FIXTURE, membershipName: "Abonament Volera · Durată fixă" }),
});

type MembershipFixtureVariant =
  | "active" | "one-session" | "unlimited" | "exhausted" | "paused" | "past-due"
  | "cancel-at-period-end" | "cancelled" | "expired" | "online-off" | "fixed" | "fixed-no-early-cancel";

function membershipAppointmentSummary(item: AppointmentActivityItem): MembershipActivityAppointmentSummary {
  return {
    activityId: item.activityId,
    locationId: item.location.locationId,
    locationName: item.location.name,
    startsAtEpochMs: item.temporal.occurredAtEpochMs,
    durationMinutes: item.temporal.durationMinutes,
    serviceNames: item.lines.map(line => line.name),
    imageUrls: item.previewMedia.length > 0
      ? item.previewMedia.map(media => media.imageUrl)
      : item.business.imageUrl ? [item.business.imageUrl] : [],
  };
}

/** Explicit UI fixtures paired with the existing synthetic session-use events.
 * They publish no redemption attribution and never change an owned allowance. */
const MEMBERSHIP_HISTORY_APPOINTMENT_IDS: Partial<Record<MembershipFixtureVariant, string>> = {
  active: "appointment-volera-membership-history-active",
  unlimited: "appointment-volera-membership-history-unlimited",
  exhausted: "appointment-volera-membership-history-exhausted",
  "online-off": "appointment-volera-membership-history-online-off",
  fixed: "appointment-volera-membership-history-fixed",
  "fixed-no-early-cancel": "appointment-volera-membership-history-fixed-no-early-cancel",
};

function membershipHistoryAppointment(
  customerAccountId: string,
  variant: MembershipFixtureVariant,
  freshness: ActivityFreshness,
): AppointmentActivityItem | null {
  const id = MEMBERSHIP_HISTORY_APPOINTMENT_IDS[variant];
  if (!id) return null;
  const calendar = new Date(freshness.observedAtEpochMs);
  const periodStart = Date.UTC(calendar.getUTCFullYear(), calendar.getUTCMonth(), 1);
  const epoch = Math.max(periodStart, freshness.observedAtEpochMs - 86_400_000);
  const source = appointment(id, customerAccountId, {
    status: "completed", group: "past", epoch, freshness,
    lines: [{ ...serviceLine({ lineId: `${id}-service`, serviceId: "andrada-evening-makeup", name: "Machiaj de seară" }),
      durationMinutes: 90, price: money(18_000) }],
    capabilities: appointmentCapabilities(createActivityPointer("appointment", id)!, "blocked"),
  });
  const validated = validateActivityItem({ ...source,
    revision: { ...source.revision, generatedAtEpochMs: freshness.observedAtEpochMs },
  });
  return validated.valid && validated.item.activityType === "appointment" ? validated.item : null;
}

/** Complete current synthetic ownership, independent of Activity filtering.
 * Start from provider-authored quota (which already includes its own usage),
 * then apply the Package atomic ledger exactly once, as Activity/Wallet do.
 * Callers must not project these current balances through that ledger again. */
export function createFrontendMembershipRedemptionOwnedFixtures(customerAccountId: string, observedAtEpochMs: number) {
  if (process.env.NODE_ENV === "production" || !isActivityIdentifier(customerAccountId)) return null;
  const freshness = fixtureFreshnessAt(observedAtEpochMs, "fresh");
  const atomic = readFrontendPackageAtomicReservationsV3(customerAccountId);
  if (!freshness || atomic.status !== "ready") return null;
  const sales = readFrontendMembershipPurchaseSales(customerAccountId);
  if (sales.status !== "ready") return null;
  const purchased = sales.sales.filter(sale => sale.owned.currentPeriodStartsAtEpochMs <= observedAtEpochMs && observedAtEpochMs < sale.owned.currentPeriodEndsAtEpochMs)
    .map(sale => ({ ...sale.owned, providerId: FRONTEND_ACTIVITY_PROVIDER_ID }));
  const current = [...(["active", "one-session", "unlimited", "fixed-no-early-cancel"] as const)
    .map((variant) => membership(customerAccountId, variant, freshness).detail.ownedMembership), ...purchased]
    .map(owned => projectFrontendPackageAtomicMembershipV3(owned, atomic.rows));
  return current.some(row => row === null) ? null : Object.freeze(current.filter(row => row !== null));
}

/** Exact legacy entry provenance; never expands the selectable inventory. */
export function createFrontendMembershipRedemptionEntryFixture(customerAccountId: string, observedAtEpochMs: number, entitlementId: string) {
  if (process.env.NODE_ENV === "production" || !isActivityIdentifier(customerAccountId)) return null;
  const variant = (["active", "one-session", "unlimited", "fixed-no-early-cancel", "fixed", "cancel-at-period-end"] as const)
    .find((row) => entitlementId === `owned-membership-volera-${row}`);
  const freshness = fixtureFreshnessAt(observedAtEpochMs, "fresh");
  const atomic = readFrontendPackageAtomicReservationsV3(customerAccountId);
  if (variant && freshness && atomic.status === "ready")
    return projectFrontendPackageAtomicMembershipV3(membership(customerAccountId, variant, freshness).detail.ownedMembership, atomic.rows);
  return createFrontendMembershipRedemptionOwnedFixtures(customerAccountId, observedAtEpochMs)?.find(row => row.entitlementId === entitlementId) ?? null;
}

/** The local sale provider publishes current ownership separately from its
 * immutable receipt. Activity explicitly bridges that publication's provider;
 * no receipt alone grants sessions and no later renewal is invented here. */
export function projectFrontendMembershipPurchaseActivity(sale: FrontendMembershipPurchaseSaleV1,
  freshness: ActivityFreshness): MembershipActivityItem | null {
  const { receipt } = sale, owned = { ...sale.owned, providerId: FRONTEND_ACTIVITY_PROVIDER_ID };
  if (owned.locationId !== LOCATION.locationId || receipt.createdAtEpochMs > freshness.observedAtEpochMs ||
    owned.currentPeriodStartsAtEpochMs > freshness.observedAtEpochMs) return null;
  // Retain the sold receipt after the independently published period ends.
  // Its last owned state is explicitly stale; time alone cannot renew it.
  if (owned.currentPeriodEndsAtEpochMs <= freshness.observedAtEpochMs) freshness = {
    status: "stale", reason: "provider-marked-stale", observedAtEpochMs: owned.currentPeriodEndsAtEpochMs - 1,
    staleAtEpochMs: owned.currentPeriodEndsAtEpochMs,
  };
  const id = receipt.orderId, pointer = createActivityPointer("membership", id);
  const revision = productRewardFingerprint({ sale, kind: "membership-current-activity" });
  if (!pointer || !revision) return null;
  const terms = receipt.purchaseDraft.membershipSnapshot.terms;
  const event: MembershipActivityDetailEvent = { eventId: `${receipt.receiptId}:purchased`, kind: "purchased",
    occurredAtEpochMs: receipt.createdAtEpochMs, title: "Abonament cumpărat", description: null, sessionDelta: null, invoiceRef: null };
  const detail: MembershipActivityDetailV1 = { schemaVersion: 1, detailRevision: revision,
    issuedAtEpochMs: freshness.observedAtEpochMs, expiresAtEpochMs: Math.min(freshness.staleAtEpochMs, owned.currentPeriodEndsAtEpochMs),
    membershipReference: receipt.orderId, ownedMembership: owned, recurringPrice: receipt.purchaseDraft.recurringQuote,
    purchasedAtEpochMs: receipt.createdAtEpochMs, servicePresentations: sale.servicePresentations,
    terms: { availability: "available", policyId: terms.termsId, policyRevision: terms.version, copy: terms.content },
    cancellationPolicy: { availability: "unavailable", policyId: null, policyRevision: null, copy: null },
    cancellationEffectiveAtEpochMs: null, accessEndsAtEpochMs: owned.expiresAtEpochMs, events: [event], purchaseReceipt: receipt };
  const total = { amountMinor: receipt.checkout.quote.firstPaymentTotalAmountMinor, currency: receipt.checkout.quote.currency };
  const item: MembershipActivityItem = { ...commerceBase("membership", id, sale.customerAccountId),
    activityType: "membership", tab: "memberships", group: "memberships", status: owned.status, detail,
    membershipReference: detail.membershipReference, name: owned.name, currentPeriodEndsAtEpochMs: owned.currentPeriodEndsAtEpochMs,
    events: [{ eventId: event.eventId, kind: "purchased", occurredAtEpochMs: event.occurredAtEpochMs }],
    temporal: { timeZone: FIXTURE_TIME_ZONE, occurredAtEpochMs: event.occurredAtEpochMs, ...fixtureLocalDateTime(event.occurredAtEpochMs), durationMinutes: null },
    payment: { status: "paid", total, paid: total, due: { ...total, amountMinor: 0 }, deposit: null, cardOnFile: true,
      allocations: total.amountMinor > 0 ? [{ allocationId: receipt.paymentReference, kind: "card", amount: total, displayHint: null }] : [] },
    capabilities: [allowedCapability("open-details", pointer, { requiresFreshData: false, requiresCompleteData: false }), allowedCapability("view-location", pointer)],
    revision: { dataRevision: FRONTEND_ACTIVITY_DATA_REVISION, entityRevision: revision, generatedAtEpochMs: freshness.observedAtEpochMs },
    freshness: { ...freshness, staleAtEpochMs: detail.expiresAtEpochMs } };
  return validateActivityItem(item).valid ? item : null;
}

function membershipSalesFreshness(freshness: ActivityFreshness, sales: readonly FrontendMembershipPurchaseSaleV1[], now: number): ActivityFreshness | null {
  const latest = Math.max(freshness.observedAtEpochMs, ...sales.map(sale => sale.receipt.createdAtEpochMs));
  return latest <= now && latest < freshness.staleAtEpochMs ? { ...freshness, observedAtEpochMs: latest } : null;
}

function projectCurrentMembershipActivity(item: MembershipActivityItem, rows: readonly FrontendPackageAtomicRowV3[], revision: string): MembershipActivityItem | null {
  const owned = projectFrontendPackageAtomicMembershipV3(item.detail.ownedMembership, rows);
  if (!owned) return null;
  const relevant = rows.filter(row => row.operation.selection.allocation.membershipComposition?.consumptions.some(use => use.entitlementId === owned.entitlementId));
  if (!relevant.length) return item;
  const operationEvents: MembershipActivityDetailEvent[] = relevant.flatMap(row => {
    const quantity = row.operation.selection.allocation.membershipComposition!.consumptions.filter(use => use.entitlementId === owned.entitlementId).reduce((sum, use) => sum + use.sessionUnits, 0);
    const used = row.state === "consumed"; const released = row.state === "released";
    const occurredAtEpochMs = used ? row.terminal!.response.packageSessionAtomicEvidence.commit.resolvedAtEpochMs : released ? row.release!.resolvedAtEpochMs : row.hold.createdAtEpochMs;
    const booked = used ? createFrontendPackageAtomicAppointmentActivity({ row,
      customerAccountId: owned.customerAccountId, observedAtEpochMs: Math.max(item.detail.issuedAtEpochMs, occurredAtEpochMs),
    }) : null;
    return [{ eventId: `${row.operation.reservationId}:${row.state}`, kind: used ? "session-used" as const : "adjusted" as const, occurredAtEpochMs,
      title: used ? "Sesiuni utilizate pentru programare" : released ? "Rezervarea sesiunilor a fost eliberată" : "Sesiuni rezervate pentru programare",
      description: null, sessionDelta: released ? quantity : -quantity, invoiceRef: null,
      ...(booked?.location.locationId === owned.locationId ? { appointment: membershipAppointmentSummary(booked) } : {}),
    }];
  });
  const events = [...operationEvents, ...item.detail.events].sort((left, right) => right.occurredAtEpochMs - left.occurredAtEpochMs);
  const projected = { ...item, detail: { ...item.detail, ownedMembership: owned, events,
    issuedAtEpochMs: Math.max(item.detail.issuedAtEpochMs, events[0].occurredAtEpochMs), detailRevision: `${item.detail.detailRevision}:atomic-${revision}` },
    revision: { ...item.revision, entityRevision: `${item.revision.entityRevision}:atomic-${revision}` } };
  return validateActivityItem(projected).valid ? projected : null;
}

/** A provider mutation advances the publication inside its existing bucket,
 * not its expiry. Detail/history and the enclosing snapshot must name that
 * same observation; future mutation evidence stays unavailable. */
function currentMembershipFreshness(freshness: ActivityFreshness, rows: readonly FrontendPackageAtomicRowV3[], now: number): ActivityFreshness | null {
  let observedAtEpochMs = freshness.observedAtEpochMs;
  for (const row of rows) {
    if (!row.operation.selection.allocation.membershipComposition?.consumptions.length) continue;
    const mutationAt = row.state === "consumed" ? row.terminal!.response.packageSessionAtomicEvidence.commit.resolvedAtEpochMs
      : row.state === "released" ? row.release!.resolvedAtEpochMs : row.hold.createdAtEpochMs;
    if (mutationAt > now) return null;
    observedAtEpochMs = Math.max(observedAtEpochMs, mutationAt);
  }
  return observedAtEpochMs < freshness.staleAtEpochMs
    ? Object.freeze({ ...freshness, observedAtEpochMs }) : null;
}

/** Exact current Activity publication shared with Wallet shortcut cards. */
export function createFrontendCurrentMembershipActivityPublications(customerAccountId: string, observedAtEpochMs: number) {
  if (process.env.NODE_ENV === "production" || !isActivityIdentifier(customerAccountId)) return null;
  const bucket = fixtureFreshnessAt(observedAtEpochMs, "fresh"); const atomic = readFrontendPackageAtomicReservationsV3(customerAccountId);
  if (!bucket || atomic.status !== "ready") return null;
  const sales = readFrontendMembershipPurchaseSales(customerAccountId);
  if (sales.status !== "ready") return null;
  const current = currentMembershipFreshness(bucket, atomic.rows, observedAtEpochMs);
  const freshness = current && membershipSalesFreshness(current, sales.sales, observedAtEpochMs);
  if (!freshness) return null;
  const items = (["active", "one-session", "unlimited", "fixed-no-early-cancel"] as const).map(variant =>
    projectCurrentMembershipActivity(membership(customerAccountId, variant, freshness), atomic.rows, atomic.revision));
  for (const sale of sales.sales) {
    if (observedAtEpochMs >= sale.owned.currentPeriodEndsAtEpochMs) continue;
    const item = projectFrontendMembershipPurchaseActivity(sale, freshness);
    items.push(item && projectCurrentMembershipActivity(item, atomic.rows, atomic.revision));
  }
  return items.some(item => item === null) ? null : items.filter((item): item is MembershipActivityItem => item !== null);
}

function membership(
  customerAccountId: string,
  variant: MembershipFixtureVariant = "active",
  freshness: ActivityFreshness = FRESH,
): MembershipActivityItem {
  const id = variant === "active" ? "membership-volera-1" : `membership-volera-${variant}`;
  const pointer = createActivityPointer("membership", id)!;
  const now = freshness.observedAtEpochMs;
  const day = 86_400_000;
  const ended = variant === "expired" || variant === "cancelled";
  // Explicit synthetic provider policy: a stable UTC calendar-month cycle.
  // Publication refreshes must not fabricate a new billing period every 15m.
  const calendar = new Date(now);
  const periodMonth = calendar.getUTCMonth() - (ended ? 1 : 0);
  const periodStart = Date.UTC(calendar.getUTCFullYear(), periodMonth, 1);
  const periodEnd = Date.UTC(calendar.getUTCFullYear(), periodMonth + 1, 1);
  const status = variant === "paused" || variant === "past-due" || ended ? variant : "active";
  const cancellationPending = variant === "cancel-at-period-end";
  const fixedTerm = variant === "fixed" || variant === "fixed-no-early-cancel";
  const canCustomerCancelRenewal =
    status === "active" &&
    !cancellationPending &&
    variant !== "fixed-no-early-cancel";
  const purchaseFixture = variant === "one-session" || variant === "unlimited" || variant === "fixed-no-early-cancel"
    ? FRONTEND_MEMBERSHIP_SCENARIO_PURCHASE_FIXTURES[variant] : FRONTEND_MEMBERSHIP_PURCHASE_FIXTURE;
  const latestKind: MembershipActivityDetailEvent["kind"] =
    variant === "paused" ? "paused" : variant === "past-due" ? "payment-failed" :
      ended ? variant : cancellationPending ? "cancellation-requested" : "session-used";
  const historyAppointment = latestKind === "session-used"
    ? membershipHistoryAppointment(customerAccountId, variant, freshness) : null;
  const events: MembershipActivityDetailEvent[] = [
    ...(variant === "one-session" ? [] : [{
      eventId: `membership-event-${variant}-${latestKind}`,
      kind: latestKind,
      occurredAtEpochMs: ended ? periodEnd : Math.max(periodStart, now - day),
      title: latestKind === "session-used" ? "Sesiune utilizată" : latestKind === "payment-failed" ? "Plată nereușită" : latestKind === "paused" ? "Abonament pus pe pauză de companie" : latestKind === "cancellation-requested" ? "Reînnoire anulată" : latestKind === "expired" ? "Abonament expirat" : "Abonament anulat",
      description: null,
      sessionDelta: latestKind === "session-used" ? (variant === "exhausted" ? -3 : -1) : null,
      invoiceRef: null,
      ...(historyAppointment ? { appointment: membershipAppointmentSummary(historyAppointment) } : {}),
    } satisfies MembershipActivityDetailEvent]),
    {
      eventId: `membership-event-${variant}-renewed`, kind: "renewed", occurredAtEpochMs: periodStart,
      title: "Abonament reînnoit", description: null, sessionDelta: null, invoiceRef: null,
    },
    {
      eventId: `membership-event-${variant}-purchased`, kind: "purchased",
      occurredAtEpochMs: FRONTEND_MEMBERSHIP_PURCHASE_FIXTURE.purchasedAtEpochMs,
      title: "Abonament cumpărat", description: null, sessionDelta: null,
      invoiceRef: { invoiceId: `membership-invoice-${variant}-purchase`, invoiceRevision: FRONTEND_MEMBERSHIP_PURCHASE_FIXTURE.invoiceRevision },
    },
  ];
  const detail: MembershipActivityDetailV1 = {
    schemaVersion: 1,
    detailRevision: `membership-detail-${variant}-v1`,
    issuedAtEpochMs: now,
    expiresAtEpochMs: freshness.staleAtEpochMs,
    membershipReference: variant === "active" ? "MEM-VOL-001" : `MEM-VOL-${variant}`,
    ownedMembership: {
      schemaVersion: 1,
      entitlementId: `owned-membership-volera-${variant}`,
      providerId: FRONTEND_ACTIVITY_PROVIDER_ID,
      customerAccountId,
      locationId: LOCATION.locationId,
      membershipId: "membership-volera-monthly",
      membershipVersion: "membership-volera-v1",
      name: purchaseFixture.membershipName,
      status,
      billingFrequency: { unit: "month", interval: 1 },
      includedServices: [
        { serviceId: "andrada-evening-makeup", name: "Machiaj de seară" },
        { serviceId: "andrada-brow-shape", name: "Stilizare sprâncene" },
        { serviceId: "andrada-bridal-makeup", name: "Machiaj de mireasă" },
      ],
      sessionAllowance: variant === "unlimited" ? { kind: "unlimited" } : { kind: "limited", sessionsPerRenewal: variant === "one-session" ? 1 : 3 },
      remainingSessions: variant === "unlimited" ? null : variant === "exhausted" ? 0 : variant === "one-session" ? 1 : 2,
      currentPeriodStartsAtEpochMs: periodStart,
      currentPeriodEndsAtEpochMs: periodEnd,
      expiresAtEpochMs: ended ? periodEnd : fixedTerm ? periodEnd + 90 * day : null,
      nextPayment: ended || cancellationPending || status === "paused" || status === "past-due" ? null : { dueAtEpochMs: periodEnd, amount: money(FRONTEND_MEMBERSHIP_PURCHASE_FIXTURE.amountMinor) },
      renewal: fixedTerm ? { kind: "fixed", remainingBillingCycles: 3 } : { kind: "until-cancelled", cancelAtPeriodEnd: ended || cancellationPending },
      onlineRedemptionEnabled: variant !== "online-off",
      cancellation: canCustomerCancelRenewal ? { canCustomerCancelRenewal: true, cancelActionId: `membership-cancel-${variant}` } : { canCustomerCancelRenewal: false },
    },
    recurringPrice: money(FRONTEND_MEMBERSHIP_PURCHASE_FIXTURE.amountMinor),
    purchasedAtEpochMs: FRONTEND_MEMBERSHIP_PURCHASE_FIXTURE.purchasedAtEpochMs,
    servicePresentations: [
      { rowId: "membership-service-evening", serviceId: "andrada-evening-makeup", variantId: null, categoryName: "Machiaj", name: "Machiaj de seară", variantName: null, durationMinutes: 90, normalPrice: money(18_000) },
      { rowId: "membership-service-brows", serviceId: "andrada-brow-shape", variantId: null, categoryName: "Sprâncene", name: "Stilizare sprâncene", variantName: null, durationMinutes: 30, normalPrice: money(6_000) },
      { rowId: "membership-service-bridal-90", serviceId: "andrada-bridal-makeup", variantId: "andrada-bridal-makeup-90-min", categoryName: "Machiaj", name: "Machiaj de mireasă", variantName: "90 minute", durationMinutes: 90, normalPrice: money(35_000) },
    ],
    terms: { availability: "available", policyId: "membership-terms-volera", policyRevision: "v1", copy: "Sesiunile incluse se reînnoiesc lunar. Serviciile suplimentare se achită separat." },
    cancellationPolicy: { availability: "available", policyId: "membership-cancellation-volera", policyRevision: "v1", copy: variant === "fixed-no-early-cancel" ? "Acest abonament are o durată fixă. Anularea anticipată de către client nu este disponibilă." : "Anularea oprește plățile viitoare. Accesul la perioada deja achitată rămâne conform condițiilor publicate de companie." },
    cancellationEffectiveAtEpochMs: ended ? periodEnd : cancellationPending ? now - day : null,
    accessEndsAtEpochMs: ended || cancellationPending || canCustomerCancelRenewal ? periodEnd : null,
    events,
  };
  const compatibleKinds = new Set(["purchased", "renewed", "paused", "resumed", "cancelled", "expired"]);
  return {
    ...commerceBase("membership", id, customerAccountId),
    revision: { dataRevision: FRONTEND_ACTIVITY_DATA_REVISION, entityRevision: `revision-${id}`, generatedAtEpochMs: now },
    activityType: "membership",
    tab: "memberships",
    group: "memberships",
    status,
    detail,
    membershipReference: detail.membershipReference,
    name: detail.ownedMembership.name,
    currentPeriodEndsAtEpochMs: periodEnd,
    events: events.filter((event) => compatibleKinds.has(event.kind)).map(({ eventId, kind, occurredAtEpochMs }) => ({ eventId, kind: kind as MembershipActivityItem["events"][number]["kind"], occurredAtEpochMs })),
    temporal: { timeZone: FIXTURE_TIME_ZONE, occurredAtEpochMs: events[0].occurredAtEpochMs, ...fixtureLocalDateTime(events[0].occurredAtEpochMs), durationMinutes: null },
    payment: payment("full-card", FRONTEND_MEMBERSHIP_PURCHASE_FIXTURE.amountMinor),
    capabilities: [allowedCapability("open-details", pointer, { requiresFreshData: false, requiresCompleteData: false }), allowedCapability("view-location", pointer)],
    freshness,
  };
}

type ProductFixtureVariant =
  | "placed"
  | "delivery-placed"
  | "shipped"
  | "ready-for-pickup"
  | "completed"
  | "cancelled"
  | "refund-pending"
  | "refund-completed"
  | "partial-refund"
  | "processing-hardening"
  | "multi-line"
  | "no-image"
  | "no-policy"
  | "invoice-unavailable"
  | "unavailable-location";

function product(
  customerAccountId: string,
  variant: ProductFixtureVariant = "placed",
): ProductActivityItem {
  const activityId = variant === "placed"
    ? "product-order-volera-1"
    : `product-order-volera-${variant}`;
  const pointer = createActivityPointer("product", activityId)!;
  const isDelivery = variant === "delivery-placed" || variant === "shipped";
  const hasRefund = variant === "refund-pending" ||
    variant === "refund-completed" || variant === "partial-refund";
  const hasInvoice = variant !== "invoice-unavailable";
  const placedAtEpochMs = Date.UTC(2026, 8, 1, 12);
  const status: ProductActivityItem["status"] = hasRefund
    ? "completed"
    : variant === "shipped" || variant === "ready-for-pickup" ||
      variant === "completed" || variant === "cancelled"
      ? variant
      : variant === "processing-hardening" ? "processing" : "placed";
  const lines: ProductActivityItem["lines"] = [
    {
      orderLineId: "order-line-volera-serum",
      productId: "product-volera-serum",
      name: "Ser Volera Luminos",
      imageUrl: variant === "no-image" ? null :
        "https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?auto=format&fit=crop&w=640&q=82",
      variantLabel: "30 ml",
      quantity: 2,
      unitPrice: money(9_000),
      lineTotal: money(18_000),
      originalUnitPrice: money(10_000),
      offerLabel: "Ofertă de lansare",
    },
    ...(variant === "multi-line" ? [{
      orderLineId: "order-line-volera-cream",
      productId: "product-volera-cream",
      name: "Cremă Volera Hidratantă",
      imageUrl: null,
      variantLabel: "50 ml",
      quantity: 1,
      unitPrice: money(5_000),
      lineTotal: money(5_000),
      originalUnitPrice: null,
      offerLabel: null,
    }] : []),
  ];
  const subtotal = lines.reduce((sum, line) => sum + line.lineTotal.amountMinor, 0);
  const discount = variant === "multi-line" ? 1_000 : 0;
  const serviceCharge = variant === "multi-line" ? 250 : 0;
  const tax = variant === "multi-line" ? 500 : 0;
  const delivery = isDelivery ? 1_900 : 0;
  const total = subtotal - discount + serviceCharge + tax + delivery;
  const locationAvailable = variant !== "unavailable-location";
  const invoice: ProductActivityItem["orderDetails"]["invoice"] = hasInvoice
    ? {
        availability: "available",
        invoiceId: `invoice-${activityId}`,
        invoiceRevision: "invoice-product-v1",
      }
    : { availability: "unavailable", invoiceId: null, invoiceRevision: null };

  return {
    ...commerceBase("product", activityId, customerAccountId),
    activityType: "product",
    tab: "products",
    group: "products",
    status,
    orderReference: variant === "placed" ? "ORDER-VOL-001" : `ORDER-VOL-${variant}`,
    temporal: {
      timeZone: FIXTURE_TIME_ZONE,
      occurredAtEpochMs: placedAtEpochMs,
      ...fixtureLocalDateTime(placedAtEpochMs),
      durationMinutes: null,
    },
    location: locationAvailable ? LOCATION : UNAVAILABLE_LOCATION,
    fulfillment: isDelivery ? "delivery" : "pickup",
    lines,
    subtotal: money(subtotal),
    tax: money(tax),
    delivery: money(delivery),
    total: money(total),
    payment: payment("full-card", total),
    refund: hasRefund ? {
      status: variant === "refund-pending" ? "pending" : "completed",
      amount: money(variant === "partial-refund" ? 9_000 : total),
      providerReference: `refund-${activityId}`,
      updatedAtEpochMs: Date.UTC(2026, 8, 2, 9),
    } : refund(),
    capabilities: [
      allowedCapability("open-details", pointer, {
        requiresFreshData: false,
        requiresCompleteData: false,
      }),
      locationAvailable
        ? allowedCapability("view-store", pointer, {
            destination: {kind: "internal-route", path: `/locations/${LOCATION.locationId}/store`},
          })
        : blockedCapability("view-store", "location-unavailable"),
      locationAvailable
        ? allowedCapability("view-location", pointer)
        : blockedCapability("view-location", "location-unavailable"),
      hasInvoice
        ? allowedCapability("view-invoice", pointer)
        : blockedCapability("view-invoice", "destination-unavailable", "hidden"),
    ],
    orderDetails: {
      schemaVersion: 1,
      store: { storeId: "store-glow-room", name: "Magazin Glow Room" },
      fulfillmentDetails: isDelivery ? {
        kind: "delivery",
        address: {
          addressLine: "Strada Exemplului 8, apartament 2",
          city: "Timișoara",
          postalCode: "300001",
          countryCode: "RO",
        },
        instructions: "Livrare standard prin curier.",
      } : {
        kind: "pickup",
        locationId: LOCATION.locationId,
        address: {
          addressLine: LOCATION.addressLine,
          city: LOCATION.city,
          postalCode: null,
          countryCode: LOCATION.countryCode,
        },
        instructions: status === "ready-for-pickup"
          ? "Comanda poate fi ridicată de la recepția locației."
          : status === "placed" ? "Așteaptă confirmarea înainte să ridici comanda." : null,
      },
      returnPolicy: variant === "no-policy" ? null : {
        policyId: "return-policy-activity-volera",
        policyRevision: "return-policy-activity-volera-v1",
        providerCopy: "Pentru întrebări despre returul produselor, contactează locația și menționează numărul comenzii.",
      },
      discount: money(discount),
      serviceCharge: money(serviceCharge),
      taxComponents: tax > 0 ? [{ taxId: "tax-order-volera", label: "Taxă pe produse", amount: money(tax) }] : [],
      invoice,
    },
  };
}

function packageActivity(
  customerAccountId: string,
  status: PackageActivityItem["status"] = "active",
): PackageActivityItem {
  const suffix = status;
  const activityId = `package-volera-${suffix}`;
  const pointer = createActivityPointer("package", activityId)!;
  const purchasedAtEpochMs = Date.UTC(2026, 5, 15, 9);
  const expiresAtEpochMs =
    status === "expired" ? Date.UTC(2026, 7, 1, 9) : Date.UTC(2027, 0, 15, 9);
  const packageTotalAmountMinor = 27_000;
  const invoiceRevision = `package-invoice-${suffix}-v1`;
  const latestEventEpochMs =
    status === "active" || status === "pending"
      ? purchasedAtEpochMs
      : status === "partially-used"
        ? Date.UTC(2026, 7, 8, 9)
        : status === "fully-used"
          ? Date.UTC(2026, 7, 18, 9)
          : status === "expired"
            ? expiresAtEpochMs
            : status === "cancelled"
              ? Date.UTC(2026, 7, 12, 9)
              : Date.UTC(2026, 7, 14, 9);
  const localDateTime = fixtureLocalDateTime(latestEventEpochMs);
  const reserveCapability = (
    benefitId: string,
    serviceId: string,
    availableQuantity: number,
  ) => {
    if (
      (status === "active" || status === "partially-used") &&
      availableQuantity > 0
    ) {
      return Object.freeze({
        availability: "allowed" as const,
        providerActionId: `reserve-${activityId}-${benefitId}-${serviceId}`,
        providerActionRevision: `reserve-${suffix}-v1`,
        reason: null,
      });
    }
    return Object.freeze({
      availability: "disabled" as const,
      providerActionId: null,
      providerActionRevision: null,
      reason:
        availableQuantity === 0
          ? ("benefit-exhausted" as const)
          : ("package-inactive" as const),
    });
  };
  const packageUsedQuantity = (initialQuantity: number) =>
    status === "fully-used"
      ? initialQuantity
      : status === "partially-used" ||
          status === "expired" ||
          status === "cancelled"
        ? Math.min(1, initialQuantity)
        : 0;
  const benefitBalance = (
    benefitId: string,
    initialQuantity: number,
    liveReservations: readonly {
      reservationId: string;
      appointmentPointer: `appointment~${string}`;
      appointmentReference: string;
      serviceId: string;
      quantity: number;
      startsAtEpochMs: number;
      timeZone: string;
      destination: { kind: "internal-route"; path: string };
    }[] = [],
  ) => {
    const usedQuantity = packageUsedQuantity(initialQuantity);
    const reservedQuantity = liveReservations.reduce(
      (sum, reservation) => sum + reservation.quantity,
      0,
    );
    return Object.freeze({
      initialQuantity,
      availableQuantity: initialQuantity - usedQuantity - reservedQuantity,
      usedQuantity,
      reservedQuantity,
      liveReservations: Object.freeze([...liveReservations]),
      balanceRevision: `balance-${benefitId}-${suffix}-v1`,
    });
  };
  const signatureReservations =
    status === "active"
      ? Object.freeze([
          Object.freeze({
            reservationId: `reservation-signature-${suffix}`,
            appointmentPointer: PACKAGE_SESSION_APPOINTMENT_FIXTURE.pointer,
            appointmentReference: PACKAGE_SESSION_APPOINTMENT_FIXTURE.reference,
            serviceId: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.service.serviceId,
            quantity: 1,
            startsAtEpochMs: PACKAGE_SESSION_APPOINTMENT_FIXTURE.startsAtEpochMs,
            timeZone: FIXTURE_TIME_ZONE,
            destination: Object.freeze({
              kind: "internal-route" as const,
              path: PACKAGE_SESSION_APPOINTMENT_FIXTURE.destinationPath,
            }),
          }),
        ])
      : Object.freeze([]);
  const signatureBalance = benefitBalance(
    `benefit-signature-${suffix}`,
    3,
    signatureReservations,
  );
  const groupServiceBalance = benefitBalance(
    `benefit-group-service-${suffix}`,
    2,
  );
  const benefits: PackageActivityItem["benefits"] = Object.freeze([
    Object.freeze({
      benefitId: `benefit-signature-${suffix}`,
      kind: "service-session" as const,
      serviceId: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.service.serviceId,
      serviceVersion: "service-signature-v1",
      name: CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.service.name,
      durationMinutes: 60,
      regularPrice: money(18_000),
      redemptionAvailability: "online-and-in-store" as const,
      balance: signatureBalance,
      reserve: reserveCapability(
        `benefit-signature-${suffix}`,
        CUSTOMER_ACCOUNT_JOURNEY_FIXTURE.service.serviceId,
        signatureBalance.availableQuantity,
      ),
    }),
    Object.freeze({
      benefitId: `benefit-group-service-${suffix}`,
      kind: "group-service" as const,
      name: "Alege un serviciu pentru sprâncene",
      redemptionAvailability: "online-and-in-store" as const,
      balance: groupServiceBalance,
      services: Object.freeze([
        Object.freeze({
          serviceId: "andrada-brow-shape",
          serviceVersion: "service-brows-v1",
          name: "Pensat și stilizare sprâncene",
          durationMinutes: 45,
          regularPrice: money(9_000),
          reserve: reserveCapability(
            `benefit-group-service-${suffix}`,
            "andrada-brow-shape",
            groupServiceBalance.availableQuantity,
          ),
        }),
        Object.freeze({
          serviceId: "andrada-brow-tint",
          serviceVersion: "service-brow-tint-v1",
          name: "Vopsit sprâncene",
          durationMinutes: 30,
          regularPrice: money(7_000),
          reserve: reserveCapability(
            `benefit-group-service-${suffix}`,
            "andrada-brow-tint",
            groupServiceBalance.availableQuantity,
          ),
        }),
      ] satisfies ActivityPackageGroupServiceBenefit["services"]),
    }),
    Object.freeze({
      benefitId: `benefit-in-store-service-${suffix}`,
      kind: "service-session" as const,
      serviceId: "andrada-in-store-consultation",
      serviceVersion: "service-in-store-consultation-v1",
      name: "Consultație disponibilă în locație",
      durationMinutes: 30,
      regularPrice: money(5_000),
      redemptionAvailability: "in-store-only" as const,
      balance: benefitBalance(`benefit-in-store-service-${suffix}`, 1),
      reserve: Object.freeze({
        availability: "disabled" as const,
        providerActionId: null,
        providerActionRevision: null,
        reason: "in-store-only" as const,
      }),
    }),
    Object.freeze({
      benefitId: `benefit-product-${suffix}`,
      kind: "product" as const,
      productId: "product-volera-serum",
      productVersion: "product-volera-serum-v1",
      name: "Ser Volera Luminos",
      regularPrice: money(7_500),
      redemptionAvailability: "in-store-only" as const,
      balance: benefitBalance(`benefit-product-${suffix}`, 1),
    }),
    Object.freeze({
      benefitId: `benefit-group-product-${suffix}`,
      kind: "group-product" as const,
      name: "Alege un produs Volera",
      redemptionAvailability: "in-store-only" as const,
      balance: benefitBalance(`benefit-group-product-${suffix}`, 1),
      products: Object.freeze([
        Object.freeze({
          productId: "product-volera-mask",
          productVersion: "product-volera-mask-v1",
          name: "Mască hidratantă Volera",
          regularPrice: money(6_000),
        }),
        Object.freeze({
          productId: "product-volera-oil",
          productVersion: "product-volera-oil-v1",
          name: "Ulei facial Volera",
          regularPrice: money(8_500),
        }),
      ] satisfies ActivityPackageGroupProductBenefit["products"]),
    }),
    Object.freeze({
      benefitId: `benefit-amount-discount-${suffix}`,
      kind: "amount-discount" as const,
      name: "Reducere valorică",
      redemptionAvailability: "in-store-only" as const,
      balance: benefitBalance(`benefit-amount-discount-${suffix}`, 1),
      discountAmount: money(2_500),
    }),
    Object.freeze({
      benefitId: `benefit-percentage-discount-${suffix}`,
      kind: "percentage-discount" as const,
      name: "Reducere procentuală",
      redemptionAvailability: "in-store-only" as const,
      balance: benefitBalance(`benefit-percentage-discount-${suffix}`, 2),
      discountBasisPoints: 1_500,
    }),
  ]);
  const lifecycleEvent =
    status === "active" || status === "pending"
      ? null
      : Object.freeze({
          eventId: `package-event-${suffix}`,
          kind:
            status === "partially-used" || status === "fully-used"
              ? ("benefit-used" as const)
              : status === "expired"
                ? ("expired" as const)
                : status === "cancelled"
                  ? ("cancelled" as const)
                  : ("refunded" as const),
          occurredAtEpochMs: latestEventEpochMs,
          benefitId:
            status === "partially-used" || status === "fully-used"
              ? `benefit-signature-${suffix}`
              : null,
          quantityDelta:
            status === "partially-used"
              ? -2
              : status === "fully-used"
                ? -3
                : null,
          title:
            status === "partially-used"
              ? "Două servicii incluse au fost utilizate"
              : status === "fully-used"
                ? "Ultimul serviciu inclus a fost utilizat"
                : status === "expired"
                  ? "Pachetul a expirat"
                  : status === "cancelled"
                    ? "Pachetul a fost anulat"
                    : "Pachetul a fost rambursat",
        });
  const purchaseEvent: ActivityPackageEvent = Object.freeze({
    eventId: `package-event-purchased-${suffix}`,
    kind: "purchased" as const,
    occurredAtEpochMs: purchasedAtEpochMs,
    benefitId: null,
    quantityDelta: null,
    title: "Pachet cumpărat",
  });
  const events: PackageActivityItem["events"] = lifecycleEvent
    ? Object.freeze([lifecycleEvent, purchaseEvent])
    : Object.freeze([purchaseEvent]);
  const base = commerceBase("package", activityId, customerAccountId);
  const paymentEvidence = payment("full-card", packageTotalAmountMinor);
  const refundEvidence: ActivityRefundEvidence =
    status === "refunded"
      ? Object.freeze({
          status: "completed",
          amount: money(packageTotalAmountMinor),
          providerReference: `refund-package-${suffix}`,
          updatedAtEpochMs: latestEventEpochMs,
        })
      : refund();

  return Object.freeze({
    ...base,
    activityType: "package",
    tab: "packages",
    group: "packages",
    status,
    temporal: Object.freeze({
      timeZone: FIXTURE_TIME_ZONE,
      occurredAtEpochMs: latestEventEpochMs,
      localDate: localDateTime.localDate,
      localTime: localDateTime.localTime,
      durationMinutes: null,
    }),
    payment: paymentEvidence,
    refund: refundEvidence,
    capabilities: Object.freeze([
      allowedCapability("open-details", pointer, {
        requiresFreshData: false,
        requiresCompleteData: false,
      }),
      allowedCapability("view-location", pointer, {
        requiresCompleteData: false,
      }),
    ]),
    revision: Object.freeze({
      dataRevision: FRONTEND_ACTIVITY_DATA_REVISION,
      entityRevision: `revision-${activityId}`,
      generatedAtEpochMs: FRONTEND_ACTIVITY_FIXTURE_NOW_EPOCH_MS,
    }),
    entitlementId: `entitlement-${suffix}`,
    soldPackageVersion: `sold-package-${suffix}-v1`,
    packageId: `catalog-package-${suffix}`,
    packageVersion: `catalog-package-${suffix}-v1`,
    packageReference: `PACK-VOL-${suffix.toUpperCase()}`,
    name: "Pachet Volera Essential",
    purchasedAtEpochMs,
    expiresAtEpochMs,
    benefits,
    events,
    invoice: Object.freeze({
      availability: "available" as const,
      invoiceId: `package-invoice-${suffix}`,
      invoiceReference: `INV-PACK-${suffix.toUpperCase()}`,
      invoiceRevision,
      issuedAtEpochMs: purchasedAtEpochMs,
      total: money(packageTotalAmountMinor),
    }),
  });
}

function packageActivities(
  customerAccountId: string,
): readonly PackageActivityItem[] {
  return Object.freeze([
    packageActivity(customerAccountId, "active"),
    packageActivity(customerAccountId, "pending"),
    packageActivity(customerAccountId, "partially-used"),
    packageActivity(customerAccountId, "fully-used"),
    packageActivity(customerAccountId, "expired"),
    packageActivity(customerAccountId, "cancelled"),
    packageActivity(customerAccountId, "refunded"),
  ]);
}

function appointmentForStatus(
  status: ActivityAppointmentStatus,
  customerAccountId: string,
): AppointmentActivityItem {
  const pastStatuses: readonly ActivityAppointmentStatus[] = [
    "completed",
    "cancelled-by-customer",
    "cancelled-by-business",
    "declined",
    "no-show",
  ];
  const group = pastStatuses.includes(status) ? "past" : "upcoming";
  return appointment(`appointment-volera-${status}`, customerAccountId, {
    status,
    group,
    attentionState:
      status === "awaiting-confirmation"
        ? {
            status: "informational",
            reason: "awaiting-confirmation",
            dueAtEpochMs: null,
          }
        : status === "rescheduled"
          ? {
              status: "informational",
              reason: "rescheduled",
              dueAtEpochMs: null,
            }
          : NO_ATTENTION,
  });
}

function requiredConsultationForm(): ActivityAppointmentFormAssignment {
  return {
    assignmentId: CUSTOMER_FORMS_ACTIVITY_REQUIRED_FIXTURE.assignmentId,
    definitionId: CUSTOMER_FORMS_ACTIVITY_REQUIRED_FIXTURE.definitionId,
    definitionTitle: CUSTOMER_FORMS_ACTIVITY_REQUIRED_FIXTURE.title.ro,
    definitionRevision:
      CUSTOMER_FORMS_ACTIVITY_REQUIRED_FIXTURE.definitionRevision,
    assignmentRevision:
      CUSTOMER_FORMS_ACTIVITY_REQUIRED_FIXTURE.providerRevision,
    status: "required",
    dueAtEpochMs: CUSTOMER_FORMS_ACTIVITY_REQUIRED_FIXTURE.dueAtEpochMs,
    completedAtEpochMs: null,
    destination: {
      kind: "internal-route",
      path: buildCustomerFormAssignmentPath(
        CUSTOMER_FORMS_ACTIVITY_REQUIRED_FIXTURE.assignmentId,
      ),
    },
  };
}

function manyItems(customerAccountId: string): readonly ActivityItem[] {
  const requiredForm = requiredConsultationForm();
  const requiredFormPointer =
    "appointment~appointment-volera-upcoming-1" as const;
  return [
    appointment("appointment-volera-upcoming-1", customerAccountId, {
      attentionState: {
        status: "action-required",
        reason: "complete-form",
        dueAtEpochMs: requiredForm.dueAtEpochMs,
      },
      capabilities: [
        ...appointmentCapabilities(requiredFormPointer),
        allowedCapability("complete-form", requiredFormPointer, {
          requiresCompleteData: false,
          destination: requiredForm.destination,
        }),
      ],
      forms: [requiredForm],
    }),
    appointment("appointment-volera-upcoming-2", customerAccountId, {
      status: "awaiting-confirmation",
      localDate: "2026-09-04",
      epoch: Date.UTC(2026, 8, 4, 9),
    }),
    packageSessionAppointment(customerAccountId),
    waitlist(customerAccountId),
    appointment("appointment-volera-past-1", customerAccountId, {
      group: "past",
      status: "completed",
    }),
    appointment("appointment-volera-cancelled-1", customerAccountId, {
      group: "past",
      status: "cancelled-by-customer",
      localDate: "2026-06-18",
      epoch: Date.UTC(2026, 5, 18, 9),
    }),
    ...giftCards(customerAccountId),
    membership(customerAccountId),
    membership(customerAccountId, "one-session"),
    membership(customerAccountId, "unlimited"),
    membership(customerAccountId, "fixed-no-early-cancel"),
    product(customerAccountId),
    ...packageActivities(customerAccountId),
  ];
}

function paginationItems(customerAccountId: string): readonly ActivityItem[] {
  return [
    appointment("appointment-volera-page-upcoming-1", customerAccountId),
    appointment("appointment-volera-page-upcoming-2", customerAccountId, {
      localDate: "2026-09-04",
      epoch: Date.UTC(2026, 8, 4, 9),
    }),
    waitlist(customerAccountId),
    ...Array.from({ length: 25 }, (_, index) => {
      const epoch = Date.UTC(2026, 5, 24, 9) - index * 24 * 60 * 60 * 1_000;
      return appointment(
        `appointment-volera-page-${index + 1}`,
        customerAccountId,
        {
          group: "past",
          status: index === 24 ? "no-show" : "completed",
          localDate: new Date(epoch).toISOString().slice(0, 10),
          epoch,
        },
      );
    }),
  ];
}

function buildScenarioItems(
  scenario: FrontendActivityFixtureScenario,
  customerAccountId: string,
): readonly ActivityItem[] {
  if (
    scenario === "empty" ||
    scenario === "loading" ||
    scenario === "error" ||
    scenario === "provider-unavailable" ||
    scenario === "auth-expired" ||
    scenario === "customer-mismatch"
  ) {
    return [];
  }

  if (scenario === "many") return manyItems(customerAccountId);
  if (scenario === "pagination") return paginationItems(customerAccountId);
  if (scenario === "waitlist") return [waitlist(customerAccountId)];
  if (scenario === "membership-redemption") return [membershipRedemptionAppointment(customerAccountId)];
  if (scenario === "waitlist-offered") {
    return [waitlist(customerAccountId, "offered")];
  }
  if (scenario === "waitlist-expired") {
    return [waitlist(customerAccountId, "expired")];
  }
  if (scenario === "gift-card") return giftCards(customerAccountId);
  if (scenario === "membership") return [membership(customerAccountId)];
  if (scenario === "membership-many") {
    return (["active", "one-session", "unlimited", "exhausted", "paused", "past-due", "cancel-at-period-end", "cancelled", "expired", "online-off", "fixed", "fixed-no-early-cancel"] as const).map((variant) => membership(customerAccountId, variant));
  }
  if (scenario.startsWith("membership-")) return [membership(customerAccountId, scenario.slice("membership-".length) as MembershipFixtureVariant)];
  if (scenario === "product") return [product(customerAccountId)];
  if (scenario === "product-many") {
    return [
      "placed", "delivery-placed", "shipped", "ready-for-pickup", "completed",
      "cancelled", "refund-pending", "partial-refund", "multi-line",
    ].map((variant) => product(customerAccountId, variant as ProductFixtureVariant));
  }
  if (scenario.startsWith("product-")) {
    return [product(customerAccountId, scenario.slice("product-".length) as ProductFixtureVariant)];
  }
  if (scenario === "package") {
    return [
      appointment("appointment-volera-upcoming-1", customerAccountId),
      packageSessionAppointment(customerAccountId),
      ...packageActivities(customerAccountId),
    ];
  }

  if (
    scenario === "awaiting-confirmation" ||
    scenario === "confirmed" ||
    scenario === "completed" ||
    scenario === "cancelled-by-customer" ||
    scenario === "cancelled-by-business" ||
    scenario === "declined" ||
    scenario === "no-show" ||
    scenario === "rescheduled"
  ) {
    return [appointmentForStatus(scenario, customerAccountId)];
  }
  if (scenario === "cancelled") {
    return [appointmentForStatus("cancelled-by-customer", customerAccountId)];
  }
  if (scenario === "in-progress-hardening") {
    return [appointmentForStatus("in-progress", customerAccountId)];
  }
  if (scenario === "past") {
    return [
      appointment("appointment-volera-past", customerAccountId, {
        group: "past",
      }),
    ];
  }
  if (scenario === "old") {
    return [
      appointment("appointment-volera-old", customerAccountId, {
        group: "past",
        status: "completed",
        localDate: "2021-02-10",
        epoch: Date.UTC(2021, 1, 10, 9),
      }),
    ];
  }
  if (scenario === "today") {
    return [
      appointment("appointment-volera-today", customerAccountId, {
        localDate: "2026-08-31",
        localTime: "16:00",
        epoch: Date.UTC(2026, 7, 31, 13),
      }),
    ];
  }
  if (scenario === "future" || scenario === "upcoming") {
    return [appointment(`appointment-volera-${scenario}`, customerAccountId)];
  }

  const options: AppointmentFixtureOptions = {};
  if (scenario === "multi-service") {
    options.lines = [
      serviceLine(),
      serviceLine({
        lineId: "line-volera-brows",
        serviceId: "andrada-brow-shape",
        name: "Pensat și stilizare sprâncene",
        staff: STAFF_DARIA,
      }),
    ];
  } else if (scenario === "add-ons") {
    options.lines = [
      serviceLine({
        addOns: [
          {
            addOnId: "premium-skin-prep",
            groupId: "andrada-makeup-extras",
            name: "Pregătire premium a tenului",
            quantity: 1,
            durationMinutes: 15,
            price: money(3_500),
          },
        ],
      }),
    ];
  } else if (scenario === "bundle") {
    options.lines = [bundleLine()];
  } else if (
    scenario === "group-booking" ||
    scenario === "participant-booking"
  ) {
    const lines: readonly [
      ActivityAppointmentLine,
      ...ActivityAppointmentLine[],
    ] = [
      serviceLine(),
      serviceLine({
        lineId: "line-volera-guest",
        serviceId: "andrada-brow-shape",
        name: "Pensat și stilizare sprâncene",
      }),
    ];
    options.lines = lines;
    options.party = {
      kind: "group",
      groupId: "group-volera-friends",
      customerRole:
        scenario === "participant-booking" ? "participant" : "organizer",
      participants: [
        {
          participantId: "participant-volera-organizer",
          displayName: "Alex",
          role: "organizer",
          ownership:
            scenario === "participant-booking" ? "other" : "current-customer",
          lineIds: ["line-volera-signature"],
        },
        {
          participantId: "participant-volera-guest",
          displayName: "Teo",
          role: "guest",
          ownership:
            scenario === "participant-booking" ? "current-customer" : "other",
          lineIds: ["line-volera-guest"],
        },
      ],
    };
    options.ownershipRelationship =
      scenario === "participant-booking" ? "participant" : "organizer";
    const groupPointer = `appointment~appointment-volera-${scenario}` as const;
    options.capabilities = [
      allowedCapability("open-details", groupPointer, {
        requiresFreshData: false,
        requiresCompleteData: false,
      }),
      blockedCapability("cancel", "provider-unavailable"),
      blockedCapability("reschedule", "policy-blocked"),
      allowedCapability("directions", groupPointer, {
        requiresCompleteData: false,
      }),
      allowedCapability("message-business", groupPointer, {
        requiresCompleteData: false,
      }),
    ];
  } else if (scenario === "any-professional") {
    options.lines = [serviceLine({ staff: STAFF_ANY })];
  } else if (scenario === "unavailable-specialist") {
    options.lines = [serviceLine({ staff: STAFF_UNAVAILABLE })];
    options.capabilities = appointmentCapabilities(
      "appointment~appointment-volera-unavailable-specialist",
      "blocked",
    );
  } else if (scenario === "unavailable-location") {
    options.location = UNAVAILABLE_LOCATION;
    options.capabilities = [
      allowedCapability(
        "open-details",
        "appointment~appointment-volera-unavailable-location",
        {
          requiresFreshData: false,
          requiresCompleteData: false,
        },
      ),
      blockedCapability("directions", "location-unavailable"),
      blockedCapability("rebook", "location-unavailable"),
    ];
  } else if (scenario === "deposit") {
    options.payment = payment("deposit");
  } else if (scenario === "full-payment") {
    options.payment = payment("full-card");
    const fullPaymentPointer = "appointment~appointment-volera-full-payment";
    options.capabilities = [
      allowedCapability("open-details", fullPaymentPointer, {
        requiresFreshData: false,
        requiresCompleteData: false,
      }),
      blockedCapability("cancel", "policy-blocked"),
      allowedCapability("reschedule", fullPaymentPointer),
      allowedCapability("directions", fullPaymentPointer, {
        requiresCompleteData: false,
      }),
      allowedCapability("message-business", fullPaymentPointer, {
        requiresCompleteData: false,
      }),
    ];
  } else if (scenario === "pay-later") {
    options.payment = payment("pay-later");
  } else if (scenario === "card-on-file") {
    options.payment = payment("card-on-file");
  } else if (scenario === "gift-card-payment") {
    options.payment = payment("gift-card");
  } else if (scenario === "split-payment") {
    options.payment = payment("split");
  } else if (scenario === "refund-pending") {
    options.payment = payment("full-card");
    options.refund = refund("pending");
    options.attentionState = {
      status: "informational",
      reason: "refund-pending",
      dueAtEpochMs: null,
    };
  } else if (scenario === "refund-completed") {
    options.payment = payment("full-card");
    options.refund = refund("completed");
  } else if (scenario === "free-cancellation") {
    options.cancellationPolicy = {
      availability: "available",
      policyId: "policy-volera-free",
      policyRevision: "revision-1",
      kind: "free",
      fee: null,
      freeUntilEpochMs: Date.UTC(2026, 7, 31, 12),
      providerCopy: "Anularea este gratuită înainte de termenul publicat.",
    };
  } else if (scenario === "cancellation-fee") {
    options.cancellationPolicy = {
      availability: "available",
      policyId: "policy-volera-fee",
      policyRevision: "revision-1",
      kind: "fee",
      fee: money(5_000),
      freeUntilEpochMs: Date.UTC(2026, 7, 31, 12),
      providerCopy: "După termenul publicat se aplică o taxă de anulare.",
    };
  } else if (scenario === "actions-blocked") {
    options.capabilities = appointmentCapabilities(
      "appointment~appointment-volera-actions-blocked",
      "blocked",
    );
  } else if (scenario === "receipt-available") {
    options.group = "past";
    options.status = "completed";
    options.capabilities = appointmentCapabilities(
      "appointment~appointment-volera-receipt-available",
      "receipt",
    );
  } else if (scenario === "receipt-unavailable") {
    options.group = "past";
    options.status = "completed";
    options.capabilities = [
      allowedCapability(
        "open-details",
        "appointment~appointment-volera-receipt-unavailable",
        {
          requiresFreshData: false,
          requiresCompleteData: false,
        },
      ),
      blockedCapability("view-receipt", "not-published", "hidden"),
    ];
  } else if (scenario === "form-required") {
    const form = requiredConsultationForm();
    const formPointer = "appointment~appointment-volera-form-required";
    options.forms = [form];
    options.attentionState = {
      status: "action-required",
      reason: "complete-form",
      dueAtEpochMs: form.dueAtEpochMs,
    };
    options.capabilities = [
      ...appointmentCapabilities(formPointer),
      allowedCapability("complete-form", formPointer, {
        requiresCompleteData: false,
        destination: form.destination,
      }),
    ];
  } else if (scenario === "discounted") {
    const discountedPayment = payment("full-card", 18_000);
    options.payment = discountedPayment;
    options.pricing = pricingBreakdown(18_000, discountedPayment, {
      adjustments: [
        {
          adjustmentId: "adjustment-volera-promotion",
          kind: "promotion",
          label: "Reducere promoțională",
          amount: money(2_000),
        },
      ],
      fees: [
        {
          feeId: "fee-volera-booking",
          kind: "booking-fee",
          label: "Taxă de rezervare",
          amount: money(500),
        },
      ],
      taxAmountMinor: 1_000,
      tipAmountMinor: 500,
    });
  } else if (scenario === "stale") {
    options.freshness = STALE;
  } else if (scenario === "partial") {
    options.completeness = PARTIAL;
  } else if (scenario === "handoff-a") {
    options.status = "confirmed";
  } else if (scenario === "handoff-b") {
    options.group = "past";
    options.status = "completed";
  }

  return [
    appointment(
      `appointment-volera-${scenario === "single-service" ? "single-service" : scenario === "single" ? "single" : scenario === "actions-allowed" ? "actions-allowed" : scenario}`,
      customerAccountId,
      options,
    ),
  ];
}

function tabForItem(item: ActivityItem): ActivityTab {
  return item.tab;
}

function searchableCopy(item: ActivityItem, legacyPackage?: PackageActivityItem): string {
  const parts = [item.business.name, item.location.name, item.activityId];
  if (item.activityType === "appointment") {
    for (const line of item.lines) {
      parts.push(line.name);
      if (line.kind === "bundle") {
        parts.push(...line.children.map((child) => child.name));
      }
    }
  } else if (item.activityType === "waitlist") {
    for (const line of item.serviceLines) {
      parts.push(line.name);
      if (line.variantName) parts.push(line.variantName);
      parts.push(...line.addOns.map((addOn) => addOn.name));
      if (line.staff.displayName) parts.push(line.staff.displayName);
    }
  } else if (
    item.activityType === "membership" ||
    item.activityType === "package"
  ) {
    parts.push(item.name);
    if (item.activityType === "membership") {
      parts.push(item.membershipReference, ...item.detail.servicePresentations.flatMap((row) => [row.name, row.variantName ?? "", row.categoryName ?? ""]));
    }
    if (item.activityType === "package") {
      if (item.ownedPackageV2) {
        const definition = item.ownedPackageV2.definition;
        const presentation = projectPackagePurchaseBenefits(definition, getPackageBenefitReferenceCatalog(definition));
        for (const benefit of presentation?.benefits ?? []) {
          parts.push(benefit.name, ...benefit.references.map(reference => reference.name));
        }
        // Legacy fixture product references are historical display evidence,
        // absent from the current Store catalog. Keep their names only for the
        // same sold Package and the exact complete authored product group.
        // This does not reconstruct a V1 balance or redemption capability.
        if (legacyPackage && validateActivityItem(legacyPackage).valid &&
          legacyPackage.ownership.customerAccountId === item.ownership.customerAccountId && legacyPackage.pointer === item.pointer &&
          legacyPackage.entitlementId === item.entitlementId && legacyPackage.soldPackageVersion === item.soldPackageVersion &&
          legacyPackage.packageId === item.packageId && legacyPackage.packageVersion === item.packageVersion &&
          legacyPackage.location.locationId === item.location.locationId && legacyPackage.purchasedAtEpochMs === item.purchasedAtEpochMs) {
          for (const benefit of definition.benefits) {
            if (benefit.kind !== "product-group-quantity") continue;
            const historical = legacyPackage.benefits.find(row => row.benefitId === benefit.benefitId);
            if (historical?.kind === "group-product" && historical.products.length === benefit.eligibleProductIds.length &&
              historical.products.every(product => benefit.eligibleProductIds.includes(product.productId))) {
              parts.push(...historical.products.map(product => product.name));
            }
          }
        }
      }
      for (const benefit of item.benefits) {
        parts.push(benefit.name);
        if (benefit.kind === "group-service") {
          parts.push(...benefit.services.map((service) => service.name));
        } else if (benefit.kind === "group-product") {
          parts.push(...benefit.products.map((product) => product.name));
        }
      }
    }
  } else if (item.activityType === "product") {
    parts.push(item.orderReference, item.orderDetails.store.name);
    for (const line of item.lines) {
      parts.push(line.name);
      if (line.variantLabel) parts.push(line.variantLabel);
    }
  } else {
    parts.push(item.detail.card.maskedCode);
    if (item.recipient) parts.push(item.recipient.displayName);
  }
  return parts.join(" ").toLocaleLowerCase("ro");
}

/** Read-only count projection for an exact current purchased Package. Reuses
 * the default provider's publication and search rules before pagination; the
 * caller remains responsible for current ownership and the `many` scenario. */
export function frontendOwnedPackageActivityMatchesQuery(owned: PackageOwnedSnapshotV2, query: string): boolean {
  try {
    if (typeof query !== "string" || query.length > ACTIVITY_SEARCH_QUERY_MAX_LENGTH) return false;
    const item = currentOwnedPackageActivity(owned, undefined, FRESH);
    return item !== null && (query.length === 0 || searchableCopy(item).includes(query.toLocaleLowerCase("ro")));
  } catch { return false; }
}

function groupsForTab(
  tab: ActivityTab,
  items: readonly ActivityItem[],
): readonly ActivityGroupSnapshot[] {
  const groupOrder: Record<ActivityTab, readonly ActivityGroup[]> = {
    appointments: ["upcoming", "waitlist", "past"],
    "gift-cards": ["gift-cards"],
    memberships: ["memberships"],
    products: ["products"],
    packages: ["packages"],
  };

  return Object.freeze(
    groupOrder[tab].map((group) =>
      Object.freeze({
        group,
        items: Object.freeze(items.filter((item) => item.group === group)),
      }),
    ),
  );
}

function cursorOffset(cursor: string | null): number | null {
  if (cursor === null) return 0;
  const match = /^activity-cursor-(\d+)$/.exec(cursor);
  if (!match) return null;
  const offset = Number(match[1]);
  return Number.isSafeInteger(offset) && offset >= 0 ? offset : null;
}

function requestKey(
  request: ActivityProviderRequest<FrontendActivityFixtureScenario>,
): string {
  return [
    request.customerAccountId,
    request.scenario,
    request.tab,
    request.cursor ?? "first",
    request.pageSize ?? "default",
    request.searchQuery ?? "",
    request.selectedPointer ?? "",
  ].join(":");
}

function isValidRequest(
  request: ActivityProviderRequest<FrontendActivityFixtureScenario>,
): boolean {
  return (
    isActivityIdentifier(request.customerAccountId) &&
    (FRONTEND_ACTIVITY_FIXTURE_SCENARIOS as readonly string[]).includes(
      request.scenario,
    ) &&
    (ACTIVITY_TABS as readonly string[]).includes(request.tab) &&
    (request.cursor === undefined ||
      request.cursor === null ||
      cursorOffset(request.cursor) !== null) &&
    (request.pageSize === undefined ||
      (Number.isSafeInteger(request.pageSize) &&
        request.pageSize > 0 &&
        request.pageSize <= ACTIVITY_PAGE_SIZE_MAX)) &&
    (request.searchQuery === undefined ||
      (request.searchQuery === request.searchQuery.trim() &&
        Array.from(request.searchQuery).length <=
          ACTIVITY_SEARCH_QUERY_MAX_LENGTH)) &&
    (request.selectedPointer === undefined ||
      request.selectedPointer === null ||
      typeof request.selectedPointer === "string") &&
    (request.requestOwnerKey === undefined ||
      isActivityIdentifier(request.requestOwnerKey)) &&
    Boolean(request.signal) &&
    typeof request.signal.aborted === "boolean" &&
    typeof request.signal.addEventListener === "function"
  );
}

function errorResult(
  key: string,
  reason: Extract<ActivityProviderResult, { status: "error" }>["reason"],
  retryable: boolean,
): ActivityProviderResult {
  return Object.freeze({
    status: "error",
    providerId: FRONTEND_ACTIVITY_PROVIDER_ID,
    requestKey: key,
    reason,
    retryable,
  });
}

function resolveSelected(
  selectedPointer: string | null | undefined,
  tab: ActivityTab,
  allItems: readonly ActivityItem[],
  customerAccountId: string,
): ActivitySelectedPointerState {
  if (!selectedPointer) {
    return Object.freeze({
      status: "none",
      pointer: null,
      item: null,
    });
  }

  const parsed = parseActivityPointer(selectedPointer);
  if (!parsed) {
    return Object.freeze({
      status: "unknown",
      pointer: selectedPointer,
      item: null,
      reason: "malformed-pointer",
    });
  }

  const item = allItems.find(
    (candidate) => candidate.pointer === parsed.pointer,
  );
  if (!item) {
    return Object.freeze({
      status: "unknown",
      pointer: selectedPointer,
      item: null,
      reason: "not-found",
    });
  }
  if (item.tab !== tab) {
    return Object.freeze({
      status: "unknown",
      pointer: selectedPointer,
      item: null,
      reason: "wrong-tab",
    });
  }
  if (item.ownership.customerAccountId !== customerAccountId) {
    return Object.freeze({
      status: "unknown",
      pointer: selectedPointer,
      item: null,
      reason: "customer-mismatch",
    });
  }
  const validated = validateActivityItem(item);
  if (!validated.valid) {
    return Object.freeze({
      status: "unknown",
      pointer: selectedPointer,
      item: null,
      reason: "not-found",
    });
  }
  return Object.freeze({
    status: "ready",
    pointer: validated.item.pointer,
    item: validated.item,
  });
}

function waitForDelay(delayMs: number, signal: AbortSignal): Promise<void> {
  if (delayMs <= 0 || signal.aborted) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(resolve, delayMs);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timeout);
        resolve();
      },
      { once: true },
    );
  });
}

type FrontendActivityProviderOptions = { delayMs?: number; now?: () => number; formsProvider?: CustomerFormsProvider; singleAppointmentSource?: FrontendSingleAppointmentPublicationSource; appointmentFormsSource?: AppointmentFormsPublicationSource };

function createFrontendActivityProvider(
  options: FrontendActivityProviderOptions,
  includeBookingPreviews: boolean,
): ActivityProvider<FrontendActivityFixtureScenario> {
  const delayMs = Math.max(0, options.delayMs ?? 0);
  const now = options.now ?? (() => Date.now());
  const formsProvider = options.formsProvider ?? frontendCustomerFormsProvider;
  const appointmentFormsSource = options.appointmentFormsSource;
  const generations = new Map<string, number>();

  const resolveActivity = async (
    request: ActivityProviderRequest<FrontendActivityFixtureScenario>,
  ): Promise<ActivityProviderResult> => {
    const key = requestKey(request);
    if (!isValidRequest(request)) {
      return errorResult(key, "invalid-request", false);
    }

    const singleMutationProof = captureCustomerOwnedSessionStorageMutationProof();
    const singleCustomer = options.singleAppointmentSource || appointmentFormsSource ? readStoredCustomerAuthSessionStrict() : null;
    const ownerKey = request.requestOwnerKey ?? request.customerAccountId;
    const generation = (generations.get(ownerKey) ?? 0) + 1;
    generations.set(ownerKey, generation);

    if (request.signal.aborted) {
      return errorResult(key, "request-aborted", true);
    }

    await waitForDelay(delayMs, request.signal);

    if (request.signal.aborted) {
      return errorResult(key, "request-aborted", true);
    }
    if (generations.get(ownerKey) !== generation) {
      return errorResult(key, "stale-request", true);
    }

    if (request.scenario === "loading") {
      return Object.freeze({
        status: "loading",
        providerId: FRONTEND_ACTIVITY_PROVIDER_ID,
        requestKey: key,
      });
    }
    if (request.scenario === "provider-unavailable") {
      return Object.freeze({
        status: "unavailable",
        providerId: FRONTEND_ACTIVITY_PROVIDER_ID,
        requestKey: key,
        reason: "activity-provider-unavailable",
      });
    }
    if (request.scenario === "auth-expired") {
      return Object.freeze({
        status: "auth-expired",
        providerId: FRONTEND_ACTIVITY_PROVIDER_ID,
        requestKey: key,
        reason: "customer-session-expired",
      });
    }
    if (request.scenario === "customer-mismatch") {
      return Object.freeze({
        status: "customer-mismatch",
        providerId: FRONTEND_ACTIVITY_PROVIDER_ID,
        requestKey: key,
        expectedCustomerAccountId: FRONTEND_ACTIVITY_FIXTURE_CUSTOMER_ID,
        receivedCustomerAccountId: request.customerAccountId,
      });
    }
    if (request.scenario === "error") {
      return errorResult(key, "provider-request-failed", true);
    }

    let publicationEpochMs: number;
    try {
      publicationEpochMs = now();
    } catch {
      return errorResult(key, "provider-response-invalid", false);
    }
    let currentFreshness = fixtureFreshnessAt(
      publicationEpochMs,
      request.scenario === "stale" ? "stale" : "fresh",
    );
    if (!currentFreshness) {
      return errorResult(key, "provider-response-invalid", false);
    }

    const scenarioItems = [...buildScenarioItems(
      request.scenario,
      request.customerAccountId,
    )];
    // Only the default local fixture surface includes visual booking rehearsals.
    // They never enter current publication, Forms, benefit or mutation sources.
    const bookingPreviews = includeBookingPreviews && (request.scenario === "many" || request.scenario === "empty")
      ? readFrontendBookingActivityPreviews(request.customerAccountId) : null;
    const singleSource = options.singleAppointmentSource && (request.scenario === "many" || request.scenario === "empty") ? options.singleAppointmentSource : null;
    const singlePublication = singleSource ? readFrontendSingleAppointmentPublicationsV1(singleSource, request.customerAccountId, publicationEpochMs) : null;
    if (singlePublication?.status === "unavailable") return errorResult(key, "provider-response-invalid", false);
    const singleItems = singlePublication?.status === "ready" ? singlePublication.publications.map(row => row.item) : [];
    for (const item of singleItems) {
      if (scenarioItems.some(existing => existing.pointer === item.pointer)) return errorResult(key, "provider-response-invalid", false);
      scenarioItems.push(item);
    }
    if (singlePublication?.status === "ready" && singleItems.length > 0) {
      currentFreshness = { ...currentFreshness, staleAtEpochMs: Math.min(currentFreshness.staleAtEpochMs,
        ...singlePublication.publications.map(row => Math.min(row.expiresAtEpochMs, row.item.freshness.staleAtEpochMs))) };
    }
    // Only the canonical shared scenario composes current Forms. Other named
    // fixtures remain isolated scenario publications, never current-owned data.
    const appointmentFormsPublication = appointmentFormsSource ? readAppointmentFormsPublicationsV1(appointmentFormsSource, request.customerAccountId, publicationEpochMs) : null;
    if (appointmentFormsPublication?.status === "unavailable") return errorResult(key, "provider-response-invalid", false);
    if (appointmentFormsPublication?.status === "ready" && appointmentFormsPublication.publications.length > 0) currentFreshness = {
      ...currentFreshness, staleAtEpochMs: Math.min(currentFreshness.staleAtEpochMs, ...appointmentFormsPublication.publications.map(row => row.expiresAtEpochMs)),
    };
    const requiresCurrentForms = ((request.scenario === "many" || singleItems.length > 0) && scenarioItems.some(item => item.activityType === "appointment" && item.forms.length > 0)) ||
      appointmentFormsPublication?.status === "ready" && appointmentFormsPublication.publications.length > 0;
    const formsResult = requiresCurrentForms
      ? await formsProvider.readOverview({ customerAccountId: request.customerAccountId, locale: "ro", signal: request.signal }).catch(() => null) : null;
    const formsReadSnapshot = formsResult?.status === "ready" ? formsResult.snapshot : null;
    const currentForms = formsReadSnapshot ? validateCustomerFormsOverviewPayload(formsReadSnapshot, request.customerAccountId) : null;
    if (request.signal.aborted || generations.get(ownerKey) !== generation) return errorResult(key, "stale-request", true);
    if (requiresCurrentForms && !currentForms) return errorResult(key, "provider-response-invalid", false);
    const formsProjection = currentForms ? projectActivityCurrentForms(scenarioItems, currentForms, request.customerAccountId) : null;
    if (currentForms && !formsProjection) return errorResult(key, "provider-response-invalid", false);
    const atomic = request.scenario === "many" || scenarioItems.some(item => item.activityType === "membership")
      ? readFrontendPackageAtomicReservationsV3(request.customerAccountId) : null;
    if (atomic) {
      if (atomic.status !== "ready") return errorResult(key, "provider-response-invalid", false);
      currentFreshness = currentMembershipFreshness(currentFreshness, atomic.rows, publicationEpochMs);
      if (!currentFreshness) return errorResult(key, "provider-response-invalid", false);
    }
    const membershipSales = request.scenario === "many" ? readFrontendMembershipPurchaseSales(request.customerAccountId) : null;
    if (membershipSales?.status === "unavailable") return errorResult(key, "provider-response-invalid", false);
    if (membershipSales?.status === "ready") {
      currentFreshness = membershipSalesFreshness(currentFreshness, membershipSales.sales, publicationEpochMs);
      if (!currentFreshness) return errorResult(key, "provider-response-invalid", false);
    }
    const publicationFreshness = currentFreshness;
    let allScenarioItems = (formsProjection?.items ?? scenarioItems).map((item) => singleItems.some(row => row.pointer === item.pointer) ? item : withCurrentFixturePublication(item, publicationFreshness));
    // Publish each authored history destination from the same refreshed fixture
    // clock as its membership summary, including isolated membership scenarios.
    const membershipHistoryItems = allScenarioItems.flatMap(item => {
      if (item.activityType !== "membership") return [];
      const variant = item.activityId === "membership-volera-1"
        ? "active" : item.activityId.slice("membership-volera-".length) as MembershipFixtureVariant;
      const booked = membershipHistoryAppointment(request.customerAccountId, variant, publicationFreshness);
      return booked && item.detail.events.some(event => event.appointment?.activityId === booked.activityId) ? [booked] : [];
    });
    allScenarioItems.push(...membershipHistoryItems);
    if (membershipSales?.status === "ready") for (const sale of membershipSales.sales) {
      const item = projectFrontendMembershipPurchaseActivity(sale, publicationFreshness);
      if (!item || allScenarioItems.some(existing => existing.pointer === item.pointer)) return errorResult(key, "provider-response-invalid", false);
      allScenarioItems.push(item);
    }
    const legacyPackages = new Map(allScenarioItems.filter((item): item is PackageActivityItem => item.activityType === "package")
      .map(item => [item.pointer, item]));
    const packageTransfers = request.scenario === "many" || request.scenario === "empty" || legacyPackages.size > 0
      ? readFrontendPackageGiftTransfers(request.customerAccountId) : null;
    if (packageTransfers?.status === "unavailable") return errorResult(key, "provider-response-invalid", false);
    if (packageTransfers?.status === "ready") {
      allScenarioItems = allScenarioItems.filter(item => item.activityType !== "package" || !packageTransfers.transfers.some(transfer =>
        transfer.state === "gifted" && activityPackageMatchesOwned(item, transfer.sourceOwned)));
    }
    const ownedInventory = request.scenario === "many" || request.scenario === "empty"
      ? readFrontendCombinedPackageOwnedInventoryV2(request.customerAccountId) : null;
    if (ownedInventory?.status === "ready") {
      // Untouched legacy presentation stays compatible. Once its independently
      // owned provider revision changes, display that exact current balance,
      // retaining separately validated finance and existing appointment links.
      const replacements = ownedInventory.entitlements.flatMap(owned => {
        const reclaimedTransfer = packageTransfers?.status === "ready" && packageTransfers.transfers.some(transfer =>
          transfer.state === "claimed" && packageGiftTransferMatchesOwned(transfer, owned));
        if (request.scenario === "empty" && !readFrontendPackageGiftClaimForOwned(owned) && !reclaimedTransfer) return [];
        // A reclaimed legacy package still uses its independently authored
        // purchase finance, even when opened from an otherwise empty scenario.
        const legacy = allScenarioItems.find(item => item.pointer === owned.activityPointer) ??
          (reclaimedTransfer ? packageActivities(request.customerAccountId).find(item => activityPackageMatchesOwned(item, owned)) : undefined);
        if (legacy && (legacy.activityType !== "package" || legacy.revision.entityRevision === owned.revision)) return [];
        const current = currentOwnedPackageActivity(owned, legacy?.activityType === "package" ? legacy : undefined, currentFreshness);
        return current ? [current] : [];
      });
      const pointers = new Set(replacements.map(item => item.pointer));
      allScenarioItems = [...allScenarioItems.filter(item => !pointers.has(item.pointer)), ...replacements];
    }
    if (atomic?.status === "ready") {
      const projected: ActivityItem[] = [];
      for (const item of allScenarioItems) {
        if (item.activityType !== "membership") { projected.push(item); continue; }
        const membership = projectCurrentMembershipActivity(item, atomic.rows, atomic.revision);
        if (!membership) return errorResult(key, "provider-response-invalid", false);
        projected.push(membership);
      }
      allScenarioItems = projected;
      const linkedMembershipAppointments = new Set(projected.flatMap(item => item.activityType === "membership"
        ? item.detail.events.flatMap(event => event.appointment ? [event.appointment.activityId] : []) : []));
      if (request.scenario === "many" || linkedMembershipAppointments.size > 0) {
        for (const row of atomic.rows) {
          if (row.state !== "consumed") continue;
          if (request.scenario !== "many" && (row.terminal?.response.status !== "accepted" ||
            !linkedMembershipAppointments.has(row.terminal.response.receipt.providerBookingId))) continue;
          const booked = createFrontendPackageAtomicAppointmentActivity({ row, customerAccountId: request.customerAccountId, observedAtEpochMs: publicationEpochMs });
          if (!booked || allScenarioItems.some(item => item.pointer === booked.pointer)) return errorResult(key, "provider-response-invalid", false);
          allScenarioItems.push(booked);
        }
      }
    }
    const actualCustomer = readStoredCustomerAuthSessionStrict();
    const giftCardOwner = frontendGiftCardProviderOwnerSnapshot(request.customerAccountId);
    const currentGiftCards = request.scenario === "many" && giftCardOwner !== null
      ? readFrontendGiftCardCurrentPresentationsV1(request.customerAccountId, publicationEpochMs) : null;
    if (currentGiftCards?.status === "unavailable") return errorResult(key, "provider-response-invalid", false);
    if (currentGiftCards?.status === "ready") allScenarioItems.push(...currentGiftCards.cards.map(card => currentOwnedGiftCardActivity(card, currentFreshness)));
    // Gift Card presentation supports the in-memory demo customer. Appointment
    // tender holds retain their separate stored-session ownership contract.
    const giftAppointments = currentGiftCards?.status === "ready" &&
      actualCustomer.status === "ready" && actualCustomer.session?.account.uuid === request.customerAccountId
      ? readFrontendGiftCardAppointmentHoldsV1(request.customerAccountId) : null;
    if (giftAppointments?.status === "unavailable") return errorResult(key, "provider-response-invalid", false);
    if (giftAppointments?.status === "ready") {
      for (const row of giftAppointments.rows) {
        if (!row.activityPublicationV1) continue; // Legacy accepted rows have no invented historical facts.
        const item = createFrontendGiftCardAppointmentActivity({ publication: row.activityPublicationV1, customerAccountId: request.customerAccountId, observedAtEpochMs: publicationEpochMs });
        if (!item || allScenarioItems.some(existing => existing.pointer === item.pointer)) return errorResult(key, "provider-response-invalid", false);
        allScenarioItems.push(item);
      }
    }
    const rewardAppointments = request.scenario === "many" ? readFrontendRewardAppointmentActivitiesV1(request.customerAccountId, publicationEpochMs) : null;
    if (rewardAppointments?.status === "unavailable") return errorResult(key, "provider-response-invalid", false);
    if (rewardAppointments?.status === "ready") {
      for (const item of rewardAppointments.items) {
        const existingIndex = allScenarioItems.findIndex(existing => existing.pointer === item.pointer);
        if (existingIndex < 0) { allScenarioItems.push(item); continue; }
        const existing = allScenarioItems[existingIndex];
        const tender = existing.activityType === "appointment" && existing.giftCardRedemption && giftAppointments?.status === "ready"
          ? giftAppointments.rows.find(row => samePackageContractData(row.activityPublicationV1, existing.giftCardRedemption)) : null;
        const joined = tender && joinFrontendRewardAppointmentGiftCardActivityV1({ rewardItem: item, giftCardItem: existing, giftCardHold: tender, now: publicationEpochMs });
        if (!joined) return errorResult(key, "provider-response-invalid", false);
        allScenarioItems[existingIndex] = joined;
      }
    }
    if (appointmentFormsPublication?.status === "ready") {
      for (const authority of appointmentFormsPublication.publications) {
        const index = allScenarioItems.findIndex(item => item.activityType === "appointment" && item.activityId === authority.activityId);
        if (index < 0 || !currentForms) return errorResult(key, "provider-response-invalid", false);
        const attached = projectActivityAppointmentFormsPublicationV1({ item: allScenarioItems[index], authority, overview: currentForms,
          customerAccountId: request.customerAccountId, nowEpochMs: publicationEpochMs });
        if (!attached) return errorResult(key, "provider-response-invalid", false);
        allScenarioItems[index] = attached;
      }
    }
    const currentProducts = request.scenario === "many" && canReadFrontendProductCurrentOrders(request.customerAccountId)
      ? readFrontendProductCurrentOrdersV1(request.customerAccountId, publicationEpochMs) : null;
    if (currentProducts?.status === "unavailable") return errorResult(key, "provider-response-invalid", false);
    for (const item of bookingPreviews ?? []) {
      if (allScenarioItems.some(existing => existing.pointer === item.pointer)) return errorResult(key, "provider-response-invalid", false);
      allScenarioItems.push(item);
    }
    const singleRevision = singlePublication?.status === "ready" ? `${FRONTEND_ACTIVITY_DATA_REVISION}:single-${singlePublication.generation}` : FRONTEND_ACTIVITY_DATA_REVISION;
    const projectedFormsRevision = formsProjection?.revision ? `${singleRevision}:${formsProjection.revision}` : singleRevision;
    const formsRevision = appointmentFormsPublication?.status === "ready" ? `${projectedFormsRevision}:${createFrontendDescendantStateOwnerKey({
      surface: "activity-forms", stage: "financial-current-publication", draftId: null, customerAccountId: request.customerAccountId,
      frozenSource: { generation: appointmentFormsPublication.generation, projections: allScenarioItems.filter((item): item is AppointmentActivityItem => item.activityType === "appointment" && !!item.formsPublicationV1).map(item => item.formsPublicationV1) },
    })}` : projectedFormsRevision;
    const ownedRevision = ownedInventory?.status === "ready" ? `${formsRevision}:${ownedInventory.revision}` : formsRevision;
    const giftRevision = currentGiftCards?.status === "ready" && currentGiftCards.cards.length > 0 ? `${ownedRevision}:gift-${currentGiftCards.revision}` : ownedRevision;
    const rewardsRevision = rewardAppointments?.status === "ready" && rewardAppointments.items.length > 0 ? `${giftRevision}:${rewardAppointments.revision}` : giftRevision;
    const membershipRevision = membershipSales?.status === "ready" && membershipSales.sales.length > 0
      ? `${rewardsRevision}:membership-${membershipSales.revision}` : rewardsRevision;
    const composedDataRevision = currentProducts?.status === "ready" && currentProducts.publication.orders.length > 0 ? `${membershipRevision}:${currentProducts.publication.revision}` : membershipRevision;
    // All composed sources share Activity's bounded opaque identifier. Keep
    // every source in the digest when composition outgrows that contract.
    const currentDataRevision = !isActivityIdentifier(composedDataRevision) || appointmentFormsSource || bookingPreviews?.length || membershipSales?.status === "ready" && membershipSales.sales.length > 0 || rewardAppointments?.status === "ready" && rewardAppointments.items.length > 0
      ? createFrontendDescendantStateOwnerKey({ surface: "customer-activity", stage: "composed-current-publication",
      draftId: null, customerAccountId: request.customerAccountId, frozenSource: bookingPreviews?.length
        ? { current: composedDataRevision, frontendBookingPreviews: bookingPreviews } : composedDataRevision }) : composedDataRevision;
    // Detail selection must repeat the exact retained root publication so it
    // does not discard the visible list/pagination for an unchanged order.
    // Every call still reads current authority; only presentation timestamps
    // are stable, and the deadline cannot outlive the provider's current window.
    const productWindowStart = Math.floor(publicationEpochMs / FRONTEND_PRODUCT_CURRENT_ORDER_FRESHNESS_WINDOW_MS) * FRONTEND_PRODUCT_CURRENT_ORDER_FRESHNESS_WINDOW_MS;
    const snapshotFreshness: ActivityFreshness = currentProducts?.status === "ready" && currentProducts.publication.orders.length > 0
      ? { status: "fresh", observedAtEpochMs: productWindowStart, staleAtEpochMs: Math.min(currentFreshness.staleAtEpochMs,
        productWindowStart + FRONTEND_PRODUCT_CURRENT_ORDER_FRESHNESS_WINDOW_MS, currentProducts.publication.validUntilEpochMs), reason: null }
      : currentFreshness;
    if (currentProducts?.status === "ready") {
      for (const sale of currentProducts.publication.orders) {
        const item = projectProductCurrentOrderActivityV1(sale, { customerAccountId: request.customerAccountId,
          observedAtEpochMs: Math.max(snapshotFreshness.observedAtEpochMs, sale.currentOrder.updatedAtEpochMs),
          validUntilEpochMs: snapshotFreshness.staleAtEpochMs, dataRevision: currentDataRevision });
        if (!item || allScenarioItems.some(existing => existing.pointer === item.pointer)) return errorResult(key, "provider-response-invalid", false);
        allScenarioItems.push(item);
      }
    }
    if (currentDataRevision !== FRONTEND_ACTIVITY_DATA_REVISION) allScenarioItems = allScenarioItems.map(item => ({ ...item, revision: { ...item.revision, dataRevision: currentDataRevision } }));
    const tabItems = allScenarioItems
      .filter((item) => tabForItem(item) === request.tab)
      .sort((left, right) =>
        left.group === "upcoming" && right.group === "upcoming"
          ? left.temporal.occurredAtEpochMs - right.temporal.occurredAtEpochMs
          : right.temporal.occurredAtEpochMs - left.temporal.occurredAtEpochMs,
      );
    const query = request.searchQuery ?? "";
    const filteredItems =
      query.length === 0
        ? tabItems
        : tabItems.filter((item) =>
            searchableCopy(item, legacyPackages.get(item.pointer)).includes(query.toLocaleLowerCase("ro")),
          );

    const alwaysPublishedItems =
      request.tab === "appointments"
        ? filteredItems.filter((item) => item.group !== "past")
        : [];
    const cursorOwnedItems =
      request.tab === "appointments"
        ? filteredItems.filter((item) => item.group === "past")
        : filteredItems;
    const offset = cursorOffset(request.cursor ?? null);
    if (offset === null || offset > cursorOwnedItems.length) {
      return errorResult(key, "invalid-request", false);
    }
    const pageSize =
      request.pageSize ?? (request.scenario === "pagination" ? 3 : 20);
    const cursorPageItems = cursorOwnedItems.slice(offset, offset + pageSize);
    const pageItems = [...alwaysPublishedItems, ...cursorPageItems];
    const nextOffset = offset + cursorPageItems.length;
    const nextCursor =
      nextOffset < cursorOwnedItems.length
        ? `activity-cursor-${nextOffset}`
        : null;
    const completeness = request.scenario === "partial" ? PARTIAL : COMPLETE;
    const freshness = snapshotFreshness;

    const rawSnapshot: ActivitySnapshot = {
      schemaVersion: ACTIVITY_SCHEMA_VERSION,
      snapshotId: `snapshot-${request.scenario}-${request.tab}-${offset}`,
      customerAccountId: request.customerAccountId,
      tab: request.tab,
      searchQuery: query,
      groups: groupsForTab(request.tab, pageItems),
      pagination: {
        cursor: request.cursor ?? null,
        nextCursor,
        pageSize,
        returnedCount: cursorPageItems.length,
        totalCount: cursorOwnedItems.length,
      },
      dataRevision: currentDataRevision,
      completeness,
      freshness,
    };

    const validated = validateActivitySnapshot(rawSnapshot);
    if (!validated.valid) {
      return errorResult(key, "provider-response-invalid", false);
    }

    const selected = resolveSelected(
      request.selectedPointer,
      request.tab,
      allScenarioItems,
      request.customerAccountId,
    );

    // Compose only one current ownership publication. Each reader returns a
    // detached snapshot, but another source read may observe a replacement
    // during composition. Do not publish a mixed Package/Membership/Gift Card
    // snapshot or retry it under a replacement session. Historical financial
    // comparisons retain their publication clock; explicit current publications
    // also recheck freshness after the final await and current-owner reads.
    let finalSingle: ReturnType<typeof readFrontendSingleAppointmentPublicationsV1> | null = null;
    let finalAppointmentForms: ReturnType<typeof readAppointmentFormsPublicationsV1> | null = null;
    try { finalSingle = singleSource ? readFrontendSingleAppointmentPublicationsV1(singleSource, request.customerAccountId, now()) : null; }
    catch { return errorResult(key, "provider-response-invalid", false); }
    try { finalAppointmentForms = appointmentFormsSource ? readAppointmentFormsPublicationsV1(appointmentFormsSource, request.customerAccountId, now()) : null; }
    catch { return errorResult(key, "provider-response-invalid", false); }
    const finalFormsResult = currentForms ? await formsProvider.readOverview({ customerAccountId: request.customerAccountId, locale: "ro", signal: request.signal }).catch(() => null) : null;
    const finalFormsReadSnapshot = finalFormsResult?.status === "ready" ? finalFormsResult.snapshot : null;
    const finalForms = finalFormsReadSnapshot ? validateCustomerFormsOverviewPayload(finalFormsReadSnapshot, request.customerAccountId) : null;
    const requiresRegisteredFormsProof = !!appointmentFormsSource || !!singleSource && (
      !!formsReadSnapshot && customerFormsOverviewReadHasPublicationProof(formsReadSnapshot) ||
      !!finalFormsReadSnapshot && customerFormsOverviewReadHasPublicationProof(finalFormsReadSnapshot));
    const finalGiftCards = currentGiftCards?.status === "ready"
      ? readFrontendGiftCardCurrentPresentationsV1(request.customerAccountId, publicationEpochMs) : null;
    const finalOwnedInventory = ownedInventory?.status === "ready"
      ? readFrontendCombinedPackageOwnedInventoryV2(request.customerAccountId) : null;
    const finalPackageTransfers = packageTransfers?.status === "ready"
      ? readFrontendPackageGiftTransfers(request.customerAccountId) : null;
    const finalAtomic = atomic?.status === "ready"
      ? readFrontendPackageAtomicReservationsV3(request.customerAccountId) : null;
    const finalGiftAppointments = giftAppointments?.status === "ready" ? readFrontendGiftCardAppointmentHoldsV1(request.customerAccountId) : null;
    const finalRewardAppointments = rewardAppointments?.status === "ready" ? readFrontendRewardAppointmentActivitiesV1(request.customerAccountId, publicationEpochMs) : null;
    const finalProducts = currentProducts?.status === "ready" ? readFrontendProductCurrentOrdersV1(request.customerAccountId, publicationEpochMs) : null;
    const finalMembershipSales = membershipSales?.status === "ready" ? readFrontendMembershipPurchaseSales(request.customerAccountId) : null;
    const finalBookingPreviews = bookingPreviews ? readFrontendBookingActivityPreviews(request.customerAccountId) : null;
    if (request.signal.aborted || generations.get(ownerKey) !== generation ||
      bookingPreviews && !samePackageContractData(bookingPreviews, finalBookingPreviews) ||
      singleSource && (!samePackageContractData(singlePublication, finalSingle) || !samePackageContractData(singleCustomer, readStoredCustomerAuthSessionStrict())) ||
      appointmentFormsSource && (!samePackageContractData(appointmentFormsPublication, finalAppointmentForms) || !samePackageContractData(singleCustomer, readStoredCustomerAuthSessionStrict())) ||
      currentForms && !samePackageContractData(currentForms, finalForms) ||
      !singleSource && !appointmentFormsSource && snapshotFreshness !== currentFreshness && now() >= snapshotFreshness.staleAtEpochMs ||
      currentProducts?.status === "ready" && !samePackageContractData(currentProducts, finalProducts) ||
      membershipSales?.status === "ready" && !samePackageContractData(membershipSales, finalMembershipSales) ||
      giftAppointments?.status === "ready" && !samePackageContractData(giftAppointments, finalGiftAppointments) ||
      rewardAppointments?.status === "ready" && !samePackageContractData(rewardAppointments, finalRewardAppointments) ||
      currentGiftCards?.status === "ready" && (!samePackageContractData(finalGiftCards, currentGiftCards) ||
        !samePackageContractData(readStoredCustomerAuthSessionStrict(), actualCustomer) ||
        frontendGiftCardProviderOwnerSnapshot(request.customerAccountId) !== giftCardOwner) ||
      ownedInventory?.status === "ready" && !samePackageContractData(finalOwnedInventory, ownedInventory) ||
      packageTransfers?.status === "ready" && !samePackageContractData(finalPackageTransfers, packageTransfers) ||
      atomic?.status === "ready" && !samePackageContractData(finalAtomic, atomic)) {
      return errorResult(key, "provider-response-invalid", false);
    }

    // Capture time after the final await and current-owner callbacks. Nothing
    // after this clock can invoke a provider, auth or storage reader.
    let finalPublicationClock: number | null = null;
    try { finalPublicationClock = singleSource || appointmentFormsSource ? now() : null; }
    catch { return errorResult(key, "provider-response-invalid", false); }
    if ((singleSource || appointmentFormsSource) && (!Number.isSafeInteger(finalPublicationClock) || finalPublicationClock! < publicationEpochMs || finalPublicationClock! >= snapshotFreshness.staleAtEpochMs) ||
      request.signal.aborted || generations.get(ownerKey) !== generation ||
      // Callback-free guards close after all current-source, owner and clock reads.
      (singleSource || appointmentFormsSource) && !customerOwnedSessionStorageMutationProofIsCurrent(singleMutationProof) ||
      singleSource && singlePublication && !frontendSingleAppointmentPublicationReadIsCurrent(singleSource, singlePublication) ||
      appointmentFormsSource && appointmentFormsPublication && !appointmentFormsPublicationReadIsCurrent(appointmentFormsSource, appointmentFormsPublication) ||
      currentForms && requiresRegisteredFormsProof && (!formsReadSnapshot || !finalFormsReadSnapshot ||
        !customerFormsOverviewReadIsCurrent(formsReadSnapshot) || !customerFormsOverviewReadIsCurrent(finalFormsReadSnapshot, formsReadSnapshot))) {
      return errorResult(key, "provider-response-invalid", false);
    }

    return Object.freeze({
      status: "ready",
      providerId: FRONTEND_ACTIVITY_PROVIDER_ID,
      requestKey: key,
      snapshot: validated.snapshot,
      selected,
    });
  };

  return Object.freeze({
    resolveActivity,
    subscribeCurrentForms: (listener: () => void) => {
      const formsUnsubscribe = formsProvider.subscribe?.(listener);
      const singleUnsubscribe = options.singleAppointmentSource?.subscribe(listener);
      const attachmentUnsubscribe = appointmentFormsSource?.subscribe(listener);
      const previewUnsubscribe = includeBookingPreviews ? subscribeFrontendBookingActivityPreviews(listener) : undefined;
      const rewardUnsubscribe = includeBookingPreviews ? subscribeFrontendRewardOperationsV1(listener) : undefined;
      if (typeof window !== "undefined") window.addEventListener(FRONTEND_MEMBERSHIP_PURCHASE_LEDGER_EVENT, listener);
      return () => {
        formsUnsubscribe?.(); singleUnsubscribe?.(); attachmentUnsubscribe?.(); previewUnsubscribe?.(); rewardUnsubscribe?.();
        if (typeof window !== "undefined") window.removeEventListener(FRONTEND_MEMBERSHIP_PURCHASE_LEDGER_EVENT, listener);
      };
    },
    async resolveActivityTabRoots(
      request: ActivityTabRootProviderRequest<FrontendActivityFixtureScenario>,
    ) {
      return Promise.all(
        ACTIVITY_TABS.map((tab) =>
          resolveActivity({
            customerAccountId: request.customerAccountId,
            scenario: request.scenario,
            tab,
            cursor: null,
            pageSize: request.pageSize,
            searchQuery: request.searchQuery,
            selectedPointer: null,
            requestOwnerKey: `${request.requestOwnerKey}-${tab}`,
            signal: request.signal,
          }),
        ),
      );
    },
  });
}

export function createFrontendFixtureActivityProvider(
  options: FrontendActivityProviderOptions = {},
): ActivityProvider<FrontendActivityFixtureScenario> {
  return createFrontendActivityProvider(options, false);
}

export const frontendFixtureActivityProvider =
  createFrontendActivityProvider({}, true);
